/**
 * Kaelmoor.io — shared constants and data tables.
 *
 * This file is the single source of truth for both the authoritative server
 * simulation and the client (rendering + UI cost display). Nothing in here may
 * depend on browser or node APIs.
 */

/**
 * Bumped whenever the wire format changes, so an old tab is turned away rather
 * than left quietly decoding the wrong bytes.
 *
 * 9: the player-stats message gained a standings block at the front and stopped
 *    carrying every kingdom in the world.
 */
export const PROTOCOL_VERSION = 9;

/* ------------------------------------------------------------------ timing */

export const TICK_RATE = 10;                 // simulation ticks per second
export const MS_PER_TICK = 1000 / TICK_RATE; // 100 ms
export const DT = 1 / TICK_RATE;             // seconds per tick

/**
 * **Territory: the whole map, five times a second, to everybody.**
 *
 * This looks like the expensive, naive choice and it is neither. Two cleverer
 * schemes were built and measured against it, and both lost:
 *
 * - **Only the viewport.** Cheapest on paper, unplayable in practice: ground
 *   the camera pans onto would have to be *fetched*, and the client would wait
 *   a round trip staring at wrongly-coloured land every time it moved.
 * - **Two rates — fast near the camera, a slow sweep for the rest.** Never
 *   fetches, so it has none of that problem, and it was built and measured
 *   (`npm run simtest`, 100 kingdoms, one hour): 2.6 KB/s against **2.4 KB/s**
 *   for simply sending everything at 5 Hz. It cost *more*, because a whole-map
 *   delta already coalesces into sorted runs beautifully, while two streams
 *   pay two headers and send the near tiles twice — and it bought a distant
 *   frontier that was up to a second out of date.
 *
 * The lesson is worth keeping: territory was never the expensive stream. In
 * the same measurement the entity stream was 10.3 KB/s and per-kingdom stats
 * were 4.0. Optimising this was the wrong target; `encodePlayers` was the
 * right one, and cutting it to 0.6 is where the whole saving came from.
 *
 * The rate itself stays at 5 Hz. Land changing hands is not interpolated — a
 * tile is one colour or the other — so this *is* the frame rate of the border,
 * and at 3 Hz a front being pushed hard visibly steps forward in 300 ms jerks.
 */
export const TERRITORY_EVERY_TICKS = 2;
/** Player stats at 2 Hz — a troop count nobody reads five times a second. */
export const PLAYERS_EVERY_TICKS = 5;

/**
 * Global pace of the strategic layer.
 *
 * Every per-second rate below (land yield, building output, taxes, population
 * and troop growth, and the upkeep that balances them) is written as
 * `literal * GAME_PACE`, so this one number decides how long a war lasts.
 * Combat, movement and the frontline itself are deliberately **not** slowed —
 * a battle should still be quick, it is the campaign that should be long.
 */
export const GAME_PACE = 1.25;

/* --------------------------------------------------------------------- map */

/**
 * The **grid**, not the world.
 *
 * `MAP_W x MAP_H` is the largest world the engine will ever hold, and every flat
 * array (ownership, terrain, auras) is this size for the whole life of a room.
 * The *world* a given match is played on is a rectangle inside it — see
 * `worldSizeFor` — and everything outside that rectangle is generated as deep
 * ocean, which is unwalkable, unclaimable and uninteresting.
 *
 * Sizing the world this way is what makes a per-match map size affordable: the
 * stride of every index calculation stays a compile-time constant, so `idx`,
 * `tileX` and `tileY` cost the same as they always did, and nothing has to be
 * reallocated when a room re-rolls into a different size.
 */
export const MAP_W = 672;
export const MAP_H = 420;
export const TILE_COUNT = MAP_W * MAP_H;
/**
 * Art pixels per tile edge — **doubled from 4 to 8.**
 *
 * Everything the player looks at is drawn from this: the terrain motifs, every
 * unit, every building. At four pixels a tile a building had a 12 × 12 box to
 * be recognisable in and a soldier had 8 × 8, which is enough for a silhouette
 * and not much else — every dwarf hall ended up looking like every other dwarf
 * hall because there was no room left to say anything after "hall".
 *
 * At eight, a tile is four times the pixels and there is room for a roofline, a
 * doorway, a chimney with smoke, a banner. That is what makes "every people's
 * buildings are genuinely different" possible rather than aspirational.
 *
 * Two things had to move with it. Zoom limits halve, because a tile is twice as
 * wide in world pixels and the same numbers would show a quarter of the map.
 * And the terrain texture is now painted in chunks, because a full world at
 * this resolution exceeds what a great many GPUs will accept as one texture.
 */
export const ART = 8;

export const idx = (x: number, y: number) => y * MAP_W + x;
export const tileX = (i: number) => i % MAP_W;
export const tileY = (i: number) => (i / MAP_W) | 0;
export const inBounds = (x: number, y: number) => x >= 0 && y >= 0 && x < MAP_W && y < MAP_H;

/**
 * How big a world is for a given number of kingdoms — **sub-linearly**.
 *
 * A world scaled in proportion to its population would be the same game every
 * time: everyone always gets the same elbow room, the same distance to their
 * neighbours, and the same length of war. Scaling by `count^0.6` instead means a
 * small match is genuinely **cramped** — neighbours in the first minute, no
 * quiet corner to develop in, a knife fight — while a full house still gets a
 * continent to campaign across, only a proportionally tighter one. Density is
 * the dial that actually changes how a match feels, so density is what varies.
 *
 * The floor is the original 384 x 240 continent, which a 20-kingdom world lands
 * on almost exactly; the ceiling is the whole grid at a full 128.
 */
export const WORLD_MIN_W = 384;
export const WORLD_MIN_H = 240;
/**
 * **Lowered from 0.6 to 0.45, which makes a crowded world bigger.**
 *
 * Mind the direction: the roster fraction is below one, so a *smaller*
 * exponent gives a *larger* map. At 0.6 the ground grew far more slowly than
 * the roster did, and every extra kingdom made the world tighter — sixty
 * realms were elbowing each other before anybody had taken a field, and the
 * whole map was claimed inside the first minute with nothing left to race for.
 *
 * A full house of 128 is unchanged either way: `frac` is 1 there and the world
 * is the whole grid. What moves is the middle, which is where nearly every
 * real match sits.
 */
export const WORLD_SCALE_EXP = 0.45;

export function worldSizeFor(kingdoms: number): { w: number; h: number } {
  const frac = Math.max(0, Math.min(1, kingdoms / MAX_PLAYERS));
  // area grows as count^0.6, so each edge grows as count^0.3
  const edge = Math.sqrt(Math.pow(frac, WORLD_SCALE_EXP));
  // rounded to 8 tiles so the terrain canvas lands on tidy pixel boundaries
  const w = Math.max(WORLD_MIN_W, Math.min(MAP_W, Math.round((MAP_W * edge) / 8) * 8));
  const h = Math.max(WORLD_MIN_H, Math.min(MAP_H, Math.round((MAP_H * edge) / 8) * 8));
  return { w, h };
}

/* ------------------------------------------------------------------- match */

/** The hard ceiling on human slots. A room's own cap is rolled inside this. */
export const MAX_PLAYERS = 128;
export const MAX_TEAMS = 8;

/**
 * Two worlds are always open, and each one re-rolls its shape after every match:
 * a **free-for-all**, where every kingdom is its own one-person team, and a
 * **team world** split into 2–6 colours. Room size is rolled too, so the map
 * never feels like the same match twice.
 */
export const ROOM_MIN_PLAYERS = 32;
export const ROOM_MAX_PLAYERS = 128;
export const ROOM_MIN_TEAMS = 2;
export const ROOM_MAX_TEAMS = 6;

/**
 * The third world is **VS AI**: everyone who joins fights on one side, and every
 * full AI kingdom on the map fights on the other, as a single coordinated enemy
 * nation rather than as the usual scattering of neutrals. It is the co-op mode —
 * the only one where the people in the lobby are guaranteed to be on your side.
 */
export enum RoomMode { FreeForAll = 0, Teams = 1, VersusAi = 2 }
export const ROOM_MODE_NAME = ['Free-for-all', 'Teams', 'VS AI'];

/** See the bottom of this file for what a Light world leaves out. */
export const LIGHT_SUFFIX = ' Light';

export const VS_AI_MIN_PLAYERS = 20;
export const VS_AI_MAX_PLAYERS = 60;
/**
 * The machines field **a couple more kingdoms than the humans** — an edge, not a
 * flood. The first cut used a floor of six, which meant a single player walked
 * into a six-to-one war; the enemy has to track the lobby, not dwarf it.
 */
export const VS_AI_EXTRA_MIN = 1;
export const VS_AI_EXTRA_MAX = 3;
export const VS_AI_MIN_KINGDOMS = 2;
/** humans take team 0, the machines team 1 */
export const VS_AI_HUMAN_TEAM = 0;
export const VS_AI_MACHINE_TEAM = 1;

export function vsAiKingdomsFor(humans: number, rand: () => number = Math.random): number {
  const extra = VS_AI_EXTRA_MIN + Math.floor(rand() * (VS_AI_EXTRA_MAX - VS_AI_EXTRA_MIN + 1));
  return Math.max(VS_AI_MIN_KINGDOMS, humans + extra);
}

export interface RoomConfig {
  mode: RoomMode;
  /**
   * The **Light** edition: the same rules with less content in them. See
   * `LIGHT_BANNED_STRUCTURES` near the bottom of this file for what it leaves
   * out and why.
   *
   * A property of the *world*, not of a player, and worlds never mix — a Light
   * lobby and a full lobby are separate rooms with separate matches, because
   * half a roster playing without mana against half a roster with it would not
   * be one game.
   */
  light: boolean;
  maxPlayers: number;
  /** how many colour teams the world splits into; free-for-all ignores it */
  teams: number;
  /**
   * Teamless AI kingdoms, or **-1 for "whatever this kind of world normally
   * fields"**. The three public worlds always use -1; only a room somebody
   * opened by hand ever names a number.
   */
  aiKingdoms: number;
  /** Monster camps, or -1 for "as many as the land will hold". */
  camps: number;
}

/**
 * A room somebody opens themselves has no business obeying the public worlds'
 * lower bounds — three friends and a handful of orcs is a perfectly good match,
 * and refusing it because the *matchmaker* likes rooms of 32 would be the
 * server's preferences overruling the player's.
 */
export const PRIVATE_MIN_PLAYERS = 2;
export const ROOM_AI_MAX = 40;
export const ROOM_CAMPS_MAX = 120;

export function rollRoomConfig(
  mode: RoomMode, rand: () => number = Math.random, light = false,
): RoomConfig {
  if (mode === RoomMode.VersusAi) {
    const span = VS_AI_MAX_PLAYERS - VS_AI_MIN_PLAYERS;
    const raw = VS_AI_MIN_PLAYERS + Math.floor(rand() * (span + 1));
    const maxPlayers = Math.max(VS_AI_MIN_PLAYERS, Math.min(VS_AI_MAX_PLAYERS, Math.round(raw / 4) * 4));
    return { mode, light, maxPlayers, teams: 2, aiKingdoms: -1, camps: -1 };
  }
  const span = ROOM_MAX_PLAYERS - ROOM_MIN_PLAYERS;
  // rounded to 8 so the lobby shows friendly numbers like 48 / 96 / 128
  const raw = ROOM_MIN_PLAYERS + Math.floor(rand() * (span + 1));
  const maxPlayers = Math.max(ROOM_MIN_PLAYERS, Math.min(ROOM_MAX_PLAYERS, Math.round(raw / 8) * 8));
  const teams = mode === RoomMode.FreeForAll
    ? maxPlayers
    : ROOM_MIN_TEAMS + Math.floor(rand() * (ROOM_MAX_TEAMS - ROOM_MIN_TEAMS + 1));
  return { mode, light, maxPlayers, teams, aiKingdoms: -1, camps: -1 };
}

/**
 * Turns whatever the create form sent into a config the server can trust.
 *
 * Every field is clamped rather than rejected: a slider that arrives as `1e9`
 * or `"cheese"` should open a sensible room, not refuse the player. Lives in
 * `shared` so the form and the server agree on the limits by construction — the
 * client cannot offer a setting the server will not honour.
 */
export function roomConfigFrom(raw: unknown): RoomConfig {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const int = (v: unknown, lo: number, hi: number, fallback: number) => {
    const n = Math.round(Number(v));
    if (!Number.isFinite(n)) return fallback;
    return Math.max(lo, Math.min(hi, n));
  };
  const mode = int(o.mode, 0, 2, RoomMode.Teams) as RoomMode;
  const maxPlayers = int(o.maxPlayers, PRIVATE_MIN_PLAYERS, ROOM_MAX_PLAYERS, 16);
  const teams = mode === RoomMode.FreeForAll ? maxPlayers
    : mode === RoomMode.VersusAi ? 2
      : int(o.teams, ROOM_MIN_TEAMS, ROOM_MAX_TEAMS, 2);
  return {
    mode,
    // a private room is whichever edition the host is playing
    light: o.light === true,
    maxPlayers,
    teams,
    aiKingdoms: int(o.aiKingdoms, 0, ROOM_AI_MAX, 4),
    camps: int(o.camps, 0, ROOM_CAMPS_MAX, 24),
  };
}

/**
 * Three kinds of kingdom share the map.
 *
 *  Human      — a real player, always on a coloured team.
 *  AiKingdom  — a full AI kingdom. Always 10 % of the human cap, teamless,
 *               painted in slate grey shades so nobody mistakes it for a team.
 *  Camp       — an orc/goblin/troll war camp: a tiny teamless realm that hates
 *               everyone (including other camps), scattered across the whole map.
 */
export enum PlayerKind { Human = 0, AiKingdom = 1, Camp = 2 }

export const AI_KINGDOM_RATIO = 0.1;          // 10 % of MAX_PLAYERS

/**
 * AI kingdoms used to be scenery with teeth: slower to recruit, taxed on every
 * tile, and reluctant to start anything. That made a room full of them a
 * landscape rather than an opponent — you could ignore a neighbour for twenty
 * minutes and find it exactly where you left it.
 *
 * They now recruit **slightly better than a player** and pay the same price for
 * ground as anyone else, and they are markedly more willing to pick a fight. The
 * handicap that remains is judgement, not arithmetic: they still refuse a war
 * they are plainly losing, and they still hold their corner rather than hunting
 * across the map. See `bots.ts` for the rest of what "smarter" means — they now
 * garrison their frontier, keep their masons working and hold men back when
 * somebody is already coming for them.
 */
export const AI_TROOP_GROWTH = 1.06;      // they now out-recruit a player slightly
export const AI_ATTACK_COST = 1.0;        // and pay no conquest tax at all
export const AI_AGGRESSION_MIN = 0.30;
export const AI_AGGRESSION_MAX = 0.66;

export const AI_KINGDOM_ID_BASE = 101;        // ids 101.. for AI kingdoms
export const CAMP_ID_BASE = 121;              // ids 121.. for monster camps
export const MAX_ENTITY_ID = 250;             // client colour tables are 256 wide

/**
 * **How long a lobby takes players before its war begins.**
 *
 * A minute rather than the old twenty-five seconds, because only one lobby is
 * open at a time now (see `lobbyModeAt`) and there is no longer any reason to
 * hurry: nobody is choosing between three half-empty rooms, so the whole minute
 * is spent filling one.
 *
 * There is no clock on the war itself: it ends when a team owns the continent.
 */
export const LOBBY_SECONDS = 60;
export const SPAWN_SECONDS = 20;

/**
 * The three modes, in the order the open lobby cycles through them.
 *
 * `RoomMode` is not used as the order directly, because the order is a
 * presentation decision and the enum is a wire format — renumbering one must
 * never silently reorder the other.
 */
export const LOBBY_ROTATION: RoomMode[] = [RoomMode.FreeForAll, RoomMode.Teams, RoomMode.VersusAi];

/**
 * **Which mode the open lobby is, right now — from the wall clock.**
 *
 * Only one public lobby is open at a time, and it takes its turn: free-for-all,
 * then teams, then VS AI, then round again. Twenty people arriving over three
 * minutes all land in the same world instead of splitting three ways into three
 * lobbies that never fill, which is the difference between a game that looks
 * alive on launch day and one that looks abandoned.
 *
 * Deriving it from the clock rather than storing it is what makes this work on
 * a **multi-core** server. Every core runs its own lobby with no shared memory;
 * if each rotated on its own schedule the browser would show all three modes at
 * once again and the whole point would be lost. `Date.now()` is the one thing
 * they already agree on, so they agree on this for free — no messages, no
 * leader, nothing to get out of step.
 *
 * It also answers "what if nobody turned up" without a special case: an empty
 * lobby is not started, it simply becomes the next mode when its slot ends.
 */
export function lobbyModeAt(now: number): RoomMode {
  const slot = Math.floor(now / (LOBBY_SECONDS * 1000));
  return LOBBY_ROTATION[((slot % LOBBY_ROTATION.length) + LOBBY_ROTATION.length) % LOBBY_ROTATION.length];
}

/** Seconds until the open lobby's turn is over — the countdown players watch. */
export function lobbySecondsLeft(now: number): number {
  const period = LOBBY_SECONDS * 1000;
  return (period - (now % period)) / 1000;
}
/**
 * Nothing waits on this any more — a finished world stays finished until the
 * last player leaves it. It survives only as the "how long ago did it end"
 * reading in the HUD.
 */
export const RESULTS_SECONDS = 20;
/**
 * How long one of the three **public** worlds will sit on a finished map before
 * reopening as a lobby, even with somebody still connected. Purely a guard
 * against an abandoned tab holding a shared world hostage; private rooms are
 * exempt, since nobody else is queueing for them.
 */
export const RESULTS_MAX_SECONDS = 600;
/**
 * A team wins by holding this share of the **contested** land.
 *
 * Ninety, not ninety-five. The last few per cent of a map are the slowest and
 * least interesting part of a war — a scattering of one-tile holdouts behind
 * ramparts, chased down by an army that has already plainly won — and five
 * points of that is minutes of a decided match. See `contestedLand` for what
 * the denominator excludes; between them, a won war now ends while it is still
 * recognisably a war.
 */
export const TEAM_WIN_SHARE = 0.90;
export const SPAWN_PROTECT_SECONDS = 60;
/**
 * **A kingdom starts small — small enough that its first push is visible.**
 *
 * Four rather than five, which is 49 tiles against 81. That is not a nerf: the
 * whole point of the opening is *growth you can see*, and doubling a hamlet
 * takes a few seconds where doubling a province takes half a minute. Paired
 * with `NEUTRAL_CLAIM_SPEED` it means one ordinary opening attack roughly
 * doubles your country inside five seconds, which is the moment the match
 * actually starts — borders touching, neighbours becoming a problem.
 *
 * It also puts more kingdoms comfortably on the same continent: a smaller disc
 * needs less clearance, so a crowded world stops being a queue for somewhere to
 * stand.
 */
export const SPAWN_RADIUS = 4;
/**
 * How far apart the world *places* kingdoms that did not choose for themselves.
 * Generous, because an auto-placed realm has nobody to argue its case.
 */
export const MIN_SPAWN_DISTANCE = 20;
/**
 * How close a **player** may choose to settle to another kingdom.
 *
 * Exactly two starting discs, edge to edge — so the tightest legal choice takes
 * no ground from anybody and still puts you on your neighbour's doorstep. The
 * old figure was twelve, which sounds similar and is not: on a crowded map it
 * meant a player clicking the coastline they wanted was told no, repeatedly,
 * with no hint of where yes lived. Deciding to found your kingdom in somebody's
 * armpit is a strategy, and refusing it is not the game's business.
 */
export const PLAYER_SPAWN_DISTANCE = SPAWN_RADIUS * 2;
/**
 * How far a spawn click is nudged to find ground that will take it.
 *
 * The same rule buildings live under: **clicking is aiming, not surveying.** A
 * player pointing at a river mouth means "somewhere about here", and hunting
 * for the exact pixel that satisfies every neighbour is not gameplay. Wide
 * enough to escape a crowded coast, small enough that you always land somewhere
 * you can see.
 */
export const SPAWN_SNAP_RADIUS = 18;

export function teamCountFor(players: number): number {
  if (players <= 8) return 2;
  if (players <= 24) return 4;
  return 8;
}

/* ----------------------------------------------------------------- terrain */

export enum Terrain {
  Ocean = 0,
  Shallow = 1,
  Plains = 2,
  Forest = 3,
  Mountain = 4,
  Ley = 5,
  /** the impassable spine of a range — see CONQUERABLE/WALKABLE below */
  Peak = 6,
}
export const TERRAIN_COUNT = 7;

export const TERRAIN_NAME = [
  'Deep Ocean', 'Shallows', 'Plains', 'Forest', 'Mountains', 'Ley Fields', 'High Peaks',
];

/*
 * **High Peaks are a wall the map builds for you.**
 *
 * Nothing crosses them: not a population attack, not a trained soldier, not a
 * transport. They are the spine of a mountain range, and the only way through a
 * range is a **pass** — a corridor of ordinary walkable Mountain that world
 * generation cuts through the spine on purpose (see `carvePasses`).
 *
 * That single unwalkable terrain is what turns a continent from an open field
 * into geography. A pass is a place worth holding: three tiles wide, ×2.1
 * defence, and the only road between two halves of a landmass. Before this, a
 * mountain range was a slow patch of ground you walked around; now it is a
 * border you can actually defend, and the fights happen where the map says they
 * should.
 *
 * Peaks are neither walkable nor claimable, so every existing rule — movement,
 * conquest, building, spawning, walls — refuses them without a line of new code.
 */
/** Can territory be painted on it at all? (Shallows only next to owned land.) */
export const CONQUERABLE = [false, true, true, true, true, true, false];
/** Can ground units walk on it? */
export const WALKABLE = [false, true, true, true, true, true, false];
/** Attack cost multiplier of the tile. */
export const TERRAIN_DEFENCE = [99, 1.7, 1.0, 1.35, 2.1, 1.15, 99];
/** Ground unit speed multiplier. */
export const TERRAIN_SPEED = [0, 0.5, 1.0, 0.75, 0.55, 0.9, 0];
/**
 * **Dry ground a kingdom can actually use.**
 *
 * Not water, and not an impassable peak. Most of the codebase used to say
 * `terrain >= Terrain.Plains` to mean this, which was exactly true until High
 * Peaks were added above Ley in the enum and quietly started passing that test.
 * Anywhere the question is "can somebody stand here, own here, build here",
 * this table is the answer and the comparison is not.
 */
export const USABLE_LAND = [false, false, true, true, true, true, false];

/* --------------------------------------------------------------- resources */

export enum Res {
  Food = 0,
  Wood = 1,
  Stone = 2,
  Mana = 3,
  Gold = 4,
}
export const RES_COUNT = 5;
export const RES_NAME = ['Food', 'Wood', 'Stone', 'Mana', 'Gold'];
export const RES_KEY = ['food', 'wood', 'stone', 'mana', 'gold'];
export const RES_COLOR = [0xe8c56a, 0xa9793f, 0xc2c6cf, 0xb887ff, 0xffd85e];

/**
 * Gold is a joker: **one gold buys one food, one wood or one stone**, taken
 * automatically when a stockpile runs short. It cannot buy **mana** — magic has
 * to be mined out of the ley fields, which is what makes them worth a war.
 */
export const GOLD_PER_RESOURCE = 1;
export const GOLD_CANNOT_BUY = [Res.Mana];

/**
 * Land yields. Food, wood and stone come out of the ground everywhere (a little
 * of everything, a lot of what the terrain is made of). **Mana is different:
 * the land never gives it away — you have to raise a shrine on a ley field.**
 *
 * **Ground is the smaller half of an economy, and buildings are the larger.**
 * These numbers were cut by well over half, and building output roughly tripled,
 * to make that true. Before, land dwarfed everything: a player who simply
 * painted tiles out-earned one who developed, buildings read as rounding error,
 * and the only economic decision in the game was "conquer more". Now a compact,
 * built-up realm can out-produce a sprawling empty one, which gives the map two
 * ways to play instead of one.
 */
export const BASE_YIELD = [0.0085, 0.0072, 0.0055, 0, 0].map(v => v * GAME_PACE);
/**
 * What a tile gives you of the resources it is *not* made of.
 *
 * This used to be 20 %, which made geography brutal: a forest kingdom simply
 * starved, because only food has an upkeep and its land barely grew any. At
 * 75 % every biome feeds and supplies you, and terrain becomes an advantage to
 * press rather than a sentence to serve. **Buildings keep the harsh rule** —
 * putting a Farm in the mountains still yields 20 % — so *where* you build is
 * still the decision that matters.
 */
export const CROSS_YIELD = 0.75;
export const TERRAIN_PRIMARY: number[] = [-1, Res.Food, Res.Food, Res.Wood, Res.Stone, -1, -1];
export const TERRAIN_OUTPUT = [0, 0.5, 1, 1, 1, 1, 0];
/** Ley crystals feed the mundane resources a little; their mana needs a shrine. */
export const LEY_ALL_FACTOR = 0.5;

/**
 * **The land pays in full again.**
 *
 * Timber and stone were briefly cut to half here, to push the answer towards
 * "work your ground" and away from "take more of it". Played, it did the
 * opposite of what it was for: a realm that drew no forest could neither
 * gather wood *nor* build its way out of the problem, so a bad map roll was
 * simply a lost match. The buildings are the right lever for rewarding
 * development — they were doubled instead (see `STRUCTURE_DEFS`) — and this
 * one is left alone so that every kingdom has a floor under it.
 */
export const LAND_YIELD_SCALE = [1, 1, 1, 1, 1];

export const TERRAIN_YIELD: number[][] = TERRAIN_PRIMARY.map((primary, t) =>
  BASE_YIELD.map((base, r) => {
    if (r === Res.Gold || r === Res.Mana || t === Terrain.Ocean) return 0;
    const land = LAND_YIELD_SCALE[r] ?? 1;
    if (t === Terrain.Ley) return base * LEY_ALL_FACTOR * TERRAIN_OUTPUT[t] * land;
    if (primary < 0) return 0;
    return base * (r === primary ? 1 : CROSS_YIELD) * TERRAIN_OUTPUT[t] * land;
  }));

/**
 * Diminishing returns on size, so a continent-sized empire never drowns in
 * resources it has no time to spend.
 */
export const INCOME_TILE_EXP = 0.82;
export const INCOME_REFERENCE_TILES = 250;

export function incomeScale(tiles: number): number {
  if (tiles <= 0) return 1;
  return Math.pow(Math.max(1, tiles) / INCOME_REFERENCE_TILES, INCOME_TILE_EXP - 1);
}

/** Raised alongside the building prices, so the opening still has a first move in it. */
export const START_RESOURCES = [320, 300, 220, 40, 300];

/* ------------------------------------------------------- population & gold */

/**
 * **Your people _are_ your army.** There is no separate population number to
 * read, feed and reason about: the men in your troop bar are the same men who
 * live in your villages, pay your taxes and eat your grain.
 *
 * Housing is a **multiplier on the ceiling, not a flat bonus to it.** A flat one
 * was worthless where it mattered: an empire holding ten thousand tiles already
 * has a cap in the tens of thousands, so a village adding a hundred and twenty
 * changed nothing you could see, and building more houses felt like it did
 * nothing at all — only taking land moved the number. As a percentage, a village
 * is worth having at every stage of the game and the decision "expand or
 * develop?" is a real one.
 *
 * `popCap` on a building is therefore **housing points**; `POP_HOUSING_PER_POINT`
 * turns them into a fraction of the ceiling, capped at `POP_HOUSING_MAX`.
 */
/*
 * Cut from 150 to 90 — one village level is +30 % of the ceiling, not +50 %.
 *
 * At half again per level a realm that did nothing but stack villages ended up
 * with a population no amount of land could justify, and the ceiling stopped
 * being a constraint anybody had to think about. Thirty per cent still makes a
 * village the strongest building in the game and still makes "develop or
 * expand?" a live question; it just no longer answers it on its own.
 */
export const POP_PER_VILLAGE_LEVEL = 90;
export const POP_HOUSING_PER_POINT = 1 / 300;    // one village level = +30 %
/*
 * **There is no ceiling on housing any more.**
 *
 * A flat cap is a lie told to a player who is still building: the twelfth
 * village costs more than the eleventh and does nothing, and nothing on screen
 * explains why. Worse, it makes the answer to "should I build another one?"
 * stop being an economic question and start being a lookup.
 *
 * What replaces it is the thing that was always supposed to be the brake and
 * was too weak to be one: **price**. Building costs now escalate geometrically
 * rather than in flat steps (see `BUILDING_COST_STEP`), so every house really
 * does raise the ceiling, and the tenth one costs what a tenth one should. The
 * limit is your treasury, which is a limit a player can see, plan around and
 * argue with.
 */
/**
 * The head tax: what one person pays you per second.
 *
 * Cut to less than a third of what it was, because it had quietly become the
 * *entire* economy. At the old rate a two-hundred-thousand-man empire earned a
 * thousand gold a second from taxes alone, against forty from every market it
 * owned — so gold arrived by breathing, houses paid for themselves, and trade
 * was decoration. Taxes are now a floor under a realm's income; **the money is
 * made in the markets and the harbours**, which are buildings you have to
 * choose to raise and can lose.
 */
export const GOLD_PER_TROOP = 0.0011 * GAME_PACE;
/**
 * **Markets and harbours tax your people; they do not print money.**
 *
 * Each level of a finished Market or Harbour raises what every one of your
 * subjects pays, and the buildings' own flat income was cut to pay for it. That
 * one change makes trade buildings behave like trade buildings: worth exactly as
 * much as the population they serve, worthless in an empty province, and the
 * reason a big realm bothers to build them at all. A flat income made a Market
 * a gold mine you parked in a corner and forgot about.
 */
export const GOLD_TAX_PER_TRADE_LEVEL = 0.07;
export const GOLD_TAX_TRADE_CAP = 1.8;      // +180 % on the head tax at most

/* ------------------------------------------------------------------ troops */

/**
 * **Land is where people can live; villages are why they do.**
 *
 * The territory term is deliberately small and heavily sub-linear now — bare
 * ground gives a realm somewhere to put people and very little more. What
 * actually fills a country is housing, which multiplies this by up to thirteen
 * (see `POP_HOUSING_PER_POINT`). Conquering without building therefore leaves
 * you with an empty map and no army to hold it, and a compact realm of well
 * levelled villages can field more soldiers than an empire twice its size.
 */
export const TROOP_CAP_BASE = 200;
export const TROOP_CAP_SCALE = 12;
export const TROOP_CAP_EXP = 0.62;
export const TROOP_FOOD_UPKEEP = 0.0032 * GAME_PACE;
export const STARVING_GROWTH_FACTOR = 0.15;
/**
 * What an empty granary costs you every second, as a share of your people.
 *
 * Tripled, and — more importantly — it is now what the HUD's people-per-second
 * actually reads. Starvation used to be a slower kind of growth: the number
 * kept ticking upward while a quiet attrition ate at the total behind it, so
 * the one moment a player most needed to be told "your realm is emptying" was
 * the moment the interface was least willing to say it. Run out of food now and
 * the rate goes **negative** and the population visibly falls.
 */
export const STARVING_ATTRITION = 0.012 * GAME_PACE;
/**
 * You start with an army, not a garrison — enough to take real ground in the
 * first minute instead of nibbling. The number is chosen so a fresh spawn sits
 * at roughly 60 % of its troop cap: right on the peak of the recruitment curve,
 * so the opening is also the fastest you will ever grow.
 */
export const START_TROOPS = 450;

/**
 * Recruitment curve.
 *
 * A realm does **not** recruit fastest when it is empty. Few people make few
 * people; a full country makes none because there is nowhere to put them. So
 * the rate follows a skewed logistic that starts slow, **peaks at 65 % of
 * capacity** and only tails off as the ceiling comes into view.
 *
 * `growthCurve` returns 0…1 (1 at the peak) and is multiplied by the cap, so
 * a bigger realm also recruits in bigger absolute numbers, and again by how
 * well fed it is (`foodFactor`).
 */
export const GROWTH_PEAK_AT = 0.65;
/** Even a nearly-wiped-out kingdom rebuilds, just slowly. */
export const GROWTH_FLOOR = 0.18;
/**
 * Fraction of the troop cap gained per second at the peak of the curve.
 *
 * Briefly halved, to make committing an army a real cost. Played, it made the
 * game slow rather than tense: the punishment for attacking landed long after
 * the decision, and the minutes spent watching a bar refill were not a choice
 * about anything. Put back — the pressure a big attack is supposed to create
 * comes from the hole it leaves in your defence (`attackCost` counts only the
 * men at home), which is felt immediately and can be exploited by somebody.
 */
export const TROOP_GROWTH_PEAK = 0.029 * GAME_PACE;

const GROWTH_EXP = GROWTH_PEAK_AT / (1 - GROWTH_PEAK_AT);
const GROWTH_NORM = Math.pow(GROWTH_PEAK_AT, GROWTH_EXP) * (1 - GROWTH_PEAK_AT);

export function growthCurve(filled: number): number {
  const x = filled <= 0 ? 0 : filled >= 1 ? 1 : filled;
  if (x >= 1) return 0;
  const shaped = (Math.pow(x, GROWTH_EXP) * (1 - x)) / GROWTH_NORM;
  return Math.max(GROWTH_FLOOR * (1 - x), shaped);
}

/**
 * A full larder makes children; an empty one makes soup.
 *
 * Not just "starving or not" — the *depth* of the granary sets the pace. A
 * kingdom sitting on more than `FOOD_COMFORT_SECONDS` of eating grows fastest;
 * one living hand-to-mouth crawls even though it is technically fed. It gives
 * Farms a reason to exist beyond avoiding disaster, and makes burning an
 * enemy's food a real strategic blow rather than an inconvenience.
 */
export const FOOD_GROWTH_MIN = 0.40;
export const FOOD_GROWTH_MAX = 1.35;
export const FOOD_COMFORT_SECONDS = 80;
/**
 * The deepest larder anyone is ever asked for. Without this ceiling a large
 * empire could never be "well fed" — its upkeep alone would want more grain
 * than the 3 000 stockpile cap allows — and every big kingdom would silently
 * recruit at the famine rate.
 */
export const FOOD_COMFORT_MAX = 800;
export const FOOD_COMFORT_MIN = 40;

export function foodFactor(store: number, upkeepPerSecond: number): number {
  const want = Math.min(FOOD_COMFORT_MAX,
    Math.max(FOOD_COMFORT_MIN, upkeepPerSecond * FOOD_COMFORT_SECONDS));
  const covered = Math.min(1, Math.max(0, store) / want);
  return FOOD_GROWTH_MIN + (FOOD_GROWTH_MAX - FOOD_GROWTH_MIN) * covered;
}

/**
 * **Villages are the recruiting building.** Beyond housing citizens, every
 * village level makes soldiers arrive faster — the growth rate, not just the
 * ceiling. Five villages carried to level three double your recruitment.
 */
export const TROOP_GROWTH_PER_VILLAGE_LEVEL = 0.10;
export const TROOP_GROWTH_VILLAGE_CAP = 1.5;     // +150 % at most

/* -------------------------------------------------------------- conquering */

/**
 * Conquest pacing.
 *
 * `ATTACK_BASE_COST` is the *price* of a tile in men and is deliberately left
 * alone by the speed constants below: taking ground should cost what it always
 * cost, it should simply happen sooner. Everything that follows tunes how many
 * tiles an army may convert per tick, never how many soldiers each one eats.
 */
/**
 * What a tile costs in men.
 *
 * Raised from 3.6 after the bots got materially better at their jobs in one
 * batch — they stopped throwing a third of their orders at walls they cannot
 * touch, and their armies started standing still long enough to actually arrive
 * somewhere. Both are strict improvements, and both made conquest cheaper in
 * practice: a full house fell from a comfortable half-hour to under twenty
 * minutes. This is the dial that buys the time back without changing what any
 * system *means* — ground simply costs what ground should cost.
 *
 * Set from the **spread across several worlds**, not from one run — see
 * `npm run simtest:spread`. A match is chaotic enough that a single seed can
 * finish in nineteen minutes or grind past fifty on the same constants, and
 * tuning against one number is tuning noise.
 */
/*
 * Cut from 3.6 to 2.5 to pay for the rest of that pass, and **left there —
 * moving it in *either* direction was tried, measured, and is worse.**
 *
 * The obvious reading of a slow match is "ground costs too much", and its
 * mirror is "ground costs too little". Both were run on the same worlds. At
 * 2.5 the five-world spread resolved every time: 31.5 · 45.1 · 45.8 · 49.9 ·
 * 52.6 minutes. At **1.8** the first world did not resolve at all inside sixty
 * minutes. At **3.4** it did not resolve inside fifty, and buried *one* kingdom
 * in the whole match.
 *
 * The reason is that this price is symmetric. Make ground cheap and the border
 * simply swings — whoever lost a province buys it back the same minute. Make it
 * dear and nobody can take anything from anybody. Neither produces the thing
 * that actually ends a war, which is one side being able to *keep* what it
 * takes.
 *
 * What did move with the pace, monotonically, in every run, was **stonework**:
 * 1276 wall segments standing in the 45-minute match, 1657 in the one that
 * never finished at 1.8, 2689 in the one that never finished at 3.4. A war that
 * cannot be won is a war whose treasuries go into ramparts, and a map full of
 * ramparts is a war that cannot be won. That is where the pace is decided — not
 * here.
 */
export const ATTACK_BASE_COST = 2.5;
export const DEFENCE_DENSITY_K = 0.8;
/**
 * A small dense kingdom should be a fortress, not an immovable object. Without
 * a ceiling, a rump state that has lost everything but its capital ends up with
 * a troops-per-tile ratio so extreme that nobody can finish it off, and the war
 * stalls around a handful of indigestible crumbs.
 */
export const DEFENCE_DENSITY_CAP = 1.6;
/**
 * **Below this much ground, the density bonus fades to nothing.**
 *
 * The ceiling above was supposed to stop a rump state being indigestible and it
 * does not, because the problem is not how *high* the ratio goes — it is that
 * `troops / max(20, tiles)` keeps paying out at the ceiling no matter how
 * little land is left. A realm down to its last few fields has its entire
 * population standing on them, so it sits at the cap permanently, and those
 * tiles are also the ones its army is standing on: terrain × race × a ×8.2
 * garrison × a ×2.6 density came to roughly a hundred and fifty troops for one
 * square of dirt.
 *
 * That is worse than expensive. An assault stops dead the moment the cheapest
 * tile left costs more than it is carrying (`Attack.run`), so every army sent
 * at the remnant died on arrival and handed its men back, the last tiles never
 * fell, `p.tiles` never reached zero — and the kingdom, its Keep gone and its
 * whole country taken, simply never died. Its soldiers stood there for ever.
 *
 * Fading the term over the last sixty tiles keeps everything the bonus was for
 * — a compact developed realm is genuinely hard to crack — and takes away the
 * one case it was never meant to cover: a corpse that cannot be buried.
 */
export const LAST_STAND_TILES = 60;

/**
 * **A realm holding this little has lost, and is told so.**
 *
 * `LAST_STAND_TILES` made a dying kingdom's last fields affordable again, which
 * fixed the corpse that could not be buried — but it did not fix the *other*
 * half of it: a realm reduced to the square its Keep stands on, or to the two
 * or three its own soldiers are standing on, is not playing a game any more.
 * It cannot recruit anything worth the name, cannot expand, cannot be a threat
 * and cannot be finished off quickly, and whoever is left mopping it up is
 * doing chores rather than fighting a war.
 *
 * So a country this small is simply over: its ground goes back to the
 * wilderness, its army lays down its arms, and the map moves on. Four tiles
 * rather than one, because "only the Keep" and "only where my men are standing"
 * are the same dead position and both should end the same way.
 *
 * Never applied while spawn protection is up, so a kingdom founded on a narrow
 * shelf of land gets its first minute like everybody else.
 */
export const COLLAPSE_TILES = 4;

export const DEFENDER_LOSS_RATIO = 0.55;
/**
 * The part of a realm's reserve that losing ground can never touch.
 *
 * Being invaded used to pin your population at zero and hold it there: every
 * tile taken drained the reserve by more than a besieged kingdom could recruit,
 * so the counter sat on 0 for the whole war and there was never anything to
 * strike back with. That is a death spiral, not a defence — the moment you fall
 * behind you can only keep falling.
 *
 * Below this share of the troop cap, taking ground still costs the *attacker*
 * full price but stops bleeding the defender. What is left keeps growing, so a
 * losing kingdom always accumulates the means to answer instead of watching a
 * zero.
 */
export const DEFENDER_LOSS_FLOOR = 0.22;
export const ALLY_ADJACENCY_DEFENCE = 0.15;
export const MIN_ATTACK_TROOPS = 12;
export const MAX_TILES_PER_TICK_MIN = 2;
/**
 * How fast one attack can advance, in tiles per tick.
 *
 * This scales with the size of the assault: a skirmish still crawls, but a
 * hundred thousand men move like a hundred thousand men. It used to be capped at
 * 3 for everybody, which quietly strangled the endgame — attacks merge per
 * target, so once the map was down to a handful of huge empires the whole world
 * could only change hands a dozen tiles a second no matter how large the armies
 * were, and every war became a staring contest.
 *
 * The floor, the ceiling and the troops-per-extra-tile rate were then all moved
 * by a factor of two together, which doubles the pace of conquest across the
 * whole range — a two-hundred-man opening raid and a hundred-thousand-man
 * endgame front both advance twice as fast as they did — without touching what a
 * tile costs in men.
 */
export const MAX_TILES_PER_TICK_MAX = 62;
export const ATTACK_TROOPS_PER_TILE_RATE = 300;

/**
 * Against a *defended* border, speed is a contest of armies — openfront's model.
 *
 * The doubling above applies to everyone, including the land-grab into empty
 * wilderness, where there is nobody to fight and slow expansion was only ever
 * dead time. Attacking a kingdom then scales that budget again by the ratio of
 * the men committed to the assault against every man the defender still has
 * alive: throw three times their strength at them and the border caves in;
 * attack a stronger neighbour with a token force and you crawl.
 *
 * This is a **speed** multiplier only. `attackCost` still charges the same men
 * for the same tile, so a fast push is not a cheap one.
 */
export const ATTACK_SPEED_RATIO_MIN = 0.4;
export const ATTACK_SPEED_RATIO_MAX = 2.5;

/**
 * **How much faster an army spreads into ground nobody owns.**
 *
 * The opening of a match is a land grab against nobody, and it is the one part
 * of the game where slowness buys nothing: there is no enemy to outmanoeuvre,
 * no decision being made, and every second of it is time before the game the
 * player came for actually starts. Borders touching is where the match begins,
 * so the sooner they touch the better.
 *
 * It is a **separate** dial from everything below on purpose. Making conquest
 * faster in general was tried and is measurably worse — a border that swings
 * cheaply just swings back, and matches stopped resolving at all (see
 * `ATTACK_BASE_COST`). This one only ever applies where `target === 0`, so the
 * scramble for empty country is quick and a war between two kingdoms is paced
 * exactly as it was.
 */
export const NEUTRAL_CLAIM_SPEED = 2.4;

export function attackSpeedRatio(attackTroops: number, defenderTroops: number): number {
  if (defenderTroops <= 0) return ATTACK_SPEED_RATIO_MAX;
  // square-rooted so a 4:1 advantage doubles the pace rather than quadrupling it
  const r = Math.sqrt(attackTroops / defenderTroops);
  return Math.max(ATTACK_SPEED_RATIO_MIN, Math.min(ATTACK_SPEED_RATIO_MAX, r));
}

/**
 * How an advancing front chooses its next tile.
 *
 * Cost is `distance-to-your-click + TERRAIN_PULL x terrain defence`. Distance
 * has to dominate: with terrain in charge, an army ordered into the mountains
 * would flow around them along the plains and never arrive, which reads as the
 * game refusing the order. Terrain now only breaks ties between tiles that are
 * about equally far, which keeps fronts organic without hijacking them.
 */
export const ATTACK_TERRAIN_PULL = 0.35;
export const ATTACK_JITTER = 0.15;
/**
 * **How strongly the front prefers to close a notch rather than push out a
 * finger.**
 *
 * Per neighbouring tile the attacker already holds, of eight. Without it the
 * only thing separating two tiles in the same shell was terrain and a coin
 * toss, so a front advanced as a ragged fringe and left single squares behind
 * it — the speckled border that makes a map look chewed rather than conquered.
 *
 * Deliberately smaller than one shell even at its maximum (8 × 0.08 = 0.64), so
 * it can only ever order tiles *within* a ring. A cohesion term strong enough
 * to jump a ring would be a front that fills its own bays before advancing
 * anywhere, which is a siege engine, not a war.
 *
 * This tidies the *advance*. Ground already left behind is dealt with by
 * `POCKET_CAPTURE_MAX` — an enclosed pocket is simply taken.
 */
export const ATTACK_COHESION_PULL = 0.08;

/**
 * **Ground you have surrounded *entirely by yourself* falls for nothing.**
 *
 * The rule and, more importantly, the two things that are not the rule:
 *
 * **A coastline is not a siege line.** Sea, river, peak and the edge of the
 * world all *disqualify* a pocket rather than sealing it. A realm with its back
 * to the mountains is not encircled — it is defended, which is what a mountain
 * is for, and half the map would otherwise fall out of somebody's hands for
 * having chosen a peninsula. Every way out has to be **your** land. That is
 * hard to arrange on a continent full of rivers and other players, which is
 * exactly why it may then take *everything* it has surrounded.
 *
 * **A rampart is a siege line, and this is the one place it differs from a
 * mountain.** It seals a pocket without excusing it: surround somebody's outer
 * ring and it is yours **up to their wall**, with what stands behind the wall
 * untouched. The alternative was tried first and is indefensible — if stonework
 * disqualified a pocket the way a peak does, **one tile of wall dropped anywhere
 * inside your realm would make you un-encircleable for the rest of the match**,
 * for the price of one wall. A wall should cost an army time; it should not
 * confer immunity to geometry.
 *
 * So: to take a walled realm's heart you must first break the wall, and to take
 * a realm sheltering behind a mountain you must first cut the ground it holds
 * into a piece the mountain is not part of — walking soldiers through it is how.
 * Both are work, and both are the work the map is supposed to ask for.
 *
 * There is **no size limit**. The condition is difficult enough on its own.
 */

/**
 * How far the flood fill will walk before giving up — a **performance rail, not
 * a rule of the game**.
 *
 * The scan is O(pocket), runs at most twice a tick, and aborts the moment it
 * finds any way out, so the ordinary case is a handful of neighbour reads. This
 * only bounds the pathological one: a genuinely sealed twenty-thousand-tile
 * empire, which is far past anything a match produces. If it ever bites, the
 * ground is taken the ordinary way — by fighting for it.
 */
export const POCKET_SCAN_MAX = 20000;
/**
 * How strongly a front opens where we already hold the advantage.
 *
 * Applied **only to the tiles touching our own border**, so it decides where an
 * assault goes in without ever letting it skip a whole shell. Ground covered by
 * our soldiers or reached by one of our Watchtowers is ground we take cheaply —
 * an army that ignored that and spread evenly was throwing men at the most
 * expensive tiles available and the cheapest ones alike.
 */
export const ATTACK_ADVANTAGE_PULL = 0.6;
/**
 * Empty wilderness is cheaper to claim than somebody's province.
 *
 * The opening minutes of a match are a land grab against nobody, and they were
 * slower than they needed to be: the map stayed a patchwork of unclaimed ground
 * long after everyone knew where they were going, and the interesting part of
 * the game — borders touching, decisions about neighbours — started late. This
 * only touches ground **nobody owns**, so war between kingdoms is paced exactly
 * as before.
 */
export const NEUTRAL_CLAIM_DISCOUNT = 0.36;
/**
 * Supply lines: every 36 000 tiles you already hold doubles the cost of taking
 * more. Tuned against the 146 000-tile continent — at 18 000 a leading empire
 * paid five times over and the endgame turned to treacle.
 */
export const OVERREACH_TILES = 36000;

/* ------------------------------------------------------------------- races */

export enum Race { Human = 0, Dwarf = 1, Elf = 2 }
export const RACE_COUNT = 3;

/**
 * **The ground a people belongs on.**
 *
 * Humans the open plain, dwarves the mountain, elves the forest. Two separate
 * things hang off this one table, and they should always agree with each other:
 *
 *  - **food.** On its own ground a race reaps at the full primary rate; on
 *    anybody else's it gets the ordinary cross-yield like everyone. This exists
 *    because food was the one resource geography could genuinely strangle.
 *    Every other resource merely came in slower off the wrong terrain, but food
 *    has an *upkeep* — a large population on land that grows none of it starves
 *    no matter how well the realm is run, so a dwarf who spawned in the
 *    mountains was punished for being a dwarf in dwarf country.
 *  - **defence.** `RACE_HOME_DEFENCE` — a people fights harder for the country
 *    it knows.
 *
 * Together they make home ground worth holding rather than merely worth
 * spawning on, and they give each people a different map: the same continent
 * has a different set of strong provinces depending on who is looking at it.
 */
export const RACE_HOME_TERRAIN: Terrain[] = [Terrain.Plains, Terrain.Mountain, Terrain.Forest];

/**
 * **What a people's own country is worth to defend — and what a stranger's costs.**
 *
 * +10 % on your own `RACE_HOME_TERRAIN`, −5 % on either of the other two
 * peoples'. A defender on his own soil is therefore **1.10 / 0.95 ≈ +16 %**
 * harder to shift than a foreigner holding the same ground, which is more of an
 * edge than the flat bonus gave — and the three land types average
 * `(1.10 + 0.95 + 0.95) / 3 = 1.00`, so the world as a whole is exactly as hard
 * to conquer as it was before.
 *
 * **That last part is the whole reason for the malus, and it was measured.**
 * The first cut was a plain +10 % at home and nothing anywhere else, on the
 * reasoning that it only touches a third of the map and so averages out to
 * about +3 %. It does not: every kingdom is *founded* on its own home terrain
 * and its villages want that ground too, so almost the entire early war is
 * fought on somebody's home soil and the real figure is close to the full ten.
 * On the hundred-kingdom world that was enough to stop the opening land-grab
 * compounding into an economy — the leader sat at 13 % for eighty minutes,
 * standing walls went from ~980 to over 2 000, and no war on the map could be
 * finished. Taking the same ten per cent back off foreign ground restores the
 * total and keeps every bit of the intent: it is still a reason to care which
 * province your border settles in, and now a reason not to overreach into
 * country that is not yours.
 */
export const RACE_HOME_DEFENCE = 0.10;
export const RACE_AWAY_DEFENCE = -0.05;

/**
 * `TERRAIN_YIELD` seen through one people's eyes: identical everywhere except
 * on their own home ground, where food comes in at the primary rate.
 *
 * Precomputed per race so the economy loop is the same one table lookup it
 * always was.
 */
export const RACE_TERRAIN_YIELD: number[][][] = Array.from({ length: RACE_COUNT }, (_, race) => {
  const home = RACE_HOME_TERRAIN[race];
  return TERRAIN_YIELD.map((row, t) => {
    if (t !== home) return row;
    const out = row.slice();
    out[Res.Food] = BASE_YIELD[Res.Food] * TERRAIN_OUTPUT[t];
    return out;
  });
});

/* -------------------------------------------------------------- race powers */

/**
 * **The thing to spend a late-game fortune on.**
 *
 * By the second half of a big match a developed realm earns faster than it can
 * possibly spend: every plot worth a farm has one, every hall is at level five,
 * and the stockpiles sit pinned at their ceiling wasting income. That is a hole
 * in the design, not a reward — so each people gets one devastating thing to
 * burn a treasury on.
 *
 * All three are the same machine with different numbers and a different name: a
 * point on the map that strikes over and over for a while, hitting only what is
 * hostile to whoever called it. Humans rain meteors over a wide area for a long
 * time; dwarves plant a siege engine that hits far harder over a tighter circle
 * and stands the longest; elves get a brief, enormous, very fast sweep.
 *
 * Paid for in **wood, stone and gold only** — never food, never mana. Those two
 * are what a realm is usually short of, and this is meant to drain a surplus,
 * not to compete with feeding your army.
 */
export interface RacePowerDef {
  key: string;
  name: string;
  /**
   * The one-word name, for the bottom bar on a phone.
   *
   * Lives here beside the full name rather than in a lookup table in the
   * interface, so a fourth people cannot arrive with a power that has no short
   * name and silently fall back to something wrong. "Storm", "Colossus" and
   * "Wrath" are what players call them anyway.
   */
  short: string;
  verb: string;
  /** how long the barrage lasts, and how often a strike lands */
  seconds: number;
  interval: number;
  /** how far from the chosen point strikes fall, and how wide each one is */
  radius: number;
  strike: number;
  damage: number;
  /**
   * Tiles per second the point of impact travels, or 0 for a barrage that
   * stands still.
   *
   * The Colossus is the only one that walks. A doomsday weapon that hammers one
   * eight-tile circle for twenty seconds flattens a field and leaves the
   * province around it untouched, which is not what thirty-six thousand buys —
   * it should cross the province it was called into, and be watched doing it.
   */
  walk: number;
  desc: string;
}

export const RACE_POWERS: RacePowerDef[] = [
  {
    key: 'meteor-storm', name: 'Meteor Storm', short: 'Storm', verb: 'Call the storm',
    seconds: 16, interval: 0.16, radius: 13, strike: 3.0, damage: 105, walk: 0,
    desc: 'The sky opens over a province. Sixteen seconds and a hundred falling rocks — the '
      + 'longest-lived of the three, and the one that keeps coming.',
  },
  {
    key: 'colossus', name: 'Runic Colossus', short: 'Colossus', verb: 'Wake the Colossus',
    seconds: 24, interval: 0.2, radius: 30, strike: 5.2, damage: 165, walk: 4.5,
    desc: 'Stone given one order: walk, and break everything. It lands where you point and then '
      + 'marches for twenty-four seconds, smashing five tiles flat at every one of its hundred '
      + 'and twenty steps. The only one of the three that comes to you.',
  },
  {
    key: 'wrath', name: 'Sylvan Wrath', short: 'Wrath', verb: 'Loose the Wrath',
    seconds: 9, interval: 0.09, radius: 16, strike: 2.6, damage: 85, walk: 0,
    desc: 'The forest answers, once. Nine seconds and a hundred bolts of green fire — enormous, '
      + 'furious, and over almost before it began.',
  },
];

/**
 * **How many shots a calling throws, whatever ring you set.**
 *
 * The count is `seconds / interval` and it does **not** change with the area —
 * that is the entire trade the ring poses. At the tightest setting a hundred
 * rocks land inside sixteen tiles and there is nothing left standing; at the
 * widest the same hundred are scattered over half a continent and it is a
 * broad, shallow ruin instead.
 *
 * They were a third of this before the ring could be opened up, which made a
 * wide setting simply a waste of thirty-six thousand rather than a choice.
 */
/**
 * **Half as many shots, spaced twice as far apart.**
 *
 * A hundred impacts inside twenty-two seconds is five a second: at the tight
 * end of the ring that is a solid wall of noise with nothing legible in it, and
 * at the wide end it is a hundred small craters nobody can follow. Fifty lands
 * with room to see each one arrive, over the same duration — the calling still
 * takes as long, it just is not a machine gun.
 *
 * Written as a spacing on the interval rather than as a smaller `seconds`, so
 * the weapon's *duration* — which is what its price and its warning are pitched
 * against — does not move.
 */
export const RACE_POWER_SHOT_SPACING = 2;

/** The gap between impacts, which is what the server actually counts down. */
export function racePowerInterval(def: RacePowerDef): number {
  return def.interval * RACE_POWER_SHOT_SPACING;
}

export function racePowerShots(def: RacePowerDef): number {
  return Math.round(def.seconds / racePowerInterval(def));
}

/**
 * **Twelve thousand of each**, and +80 % every time after.
 *
 * Thirty-six thousand a shot. Deliberately far past what a realm can even hold:
 * the ceiling is 3 000 a resource before Warehouses, so calling this once needs
 * six levels of storage and calling it twice needs thirteen. That is the whole
 * design — a late-game surplus finally has somewhere to go, and the Warehouse
 * stops being a curiosity and becomes the building that unlocks the endgame.
 */
export const RACE_POWER_COST = [0, 12000, 12000, 0, 12000];
export const RACE_POWER_STEP = 0.8;
/**
 * **How far you may reach with it: anywhere.**
 *
 * It used to be ninety tiles from your realm's centre, and the rule made no
 * sense from either end. What you are paying for is thirty-six thousand of
 * three resources — the whole late game's surplus — and the answer to "may I
 * hit them" was a ruler laid across the map rather than anything about the
 * enemy, your people, or the war. Worse, it punished exactly the kingdom the
 * weapon exists for: a realm squeezed into a corner has a *small* centroid
 * reach and can least afford the one thing that opens a rampart.
 *
 * A number past the diagonal of the largest world, so the check can stay where
 * it is and simply never bite.
 */
export const RACE_POWER_RANGE = 100000;

/**
 * **How wide a bite you take, chosen with the scroll wheel before you fire.**
 *
 * The barrage always throws the *same number of shots*: the ring only decides
 * how far apart they land. Wind it in and the same rock falls on the same few
 * fields over and over, cratering a capital to nothing; wind it out and the
 * same twelve seconds are spread thin across a whole province, tearing a wide
 * ragged hole rather than a deep one.
 *
 * That is the decision this weapon is supposed to pose, and it used to have no
 * answer at all — a power aimed at a kingdom simply rained on *everything they
 * owned*, so the only choice was which banner to point at.
 *
 * The ring you see is **not** the area: it is clipped to what the target
 * actually holds, so a strike aimed across a border is drawn — and lands — only
 * on their side of it.
 */
export const RACE_POWER_AREA_MIN = 16;
/**
 * And at full stretch it covers **more than half the land in the world**.
 *
 * A circle of 150 tiles is about seventy thousand squares — on a 129 000-tile
 * continent that is over half of everything anybody owns. Nothing else in the
 * game operates at that scale, and it should not: this is the one button that
 * is allowed to be absurd, because it costs thirty-six thousand and because the
 * shots are spread so thin at that size that it becomes a wide shallow scar
 * rather than a killing blow. Choosing between the two is the decision.
 */
export const RACE_POWER_AREA_MAX = 150;
/**
 * The wheel moves it by a *share* of what it currently is, not a fixed number
 * of tiles — 16 to 150 in fixed steps of two would be sixty-seven turns of the
 * wheel, and a step that felt right at the top would be useless at the bottom.
 */
export const RACE_POWER_AREA_STEP = 0.18;

export function racePowerCost(uses: number): number[] {
  const scale = 1 + Math.max(0, uses) * RACE_POWER_STEP;
  return RACE_POWER_COST.map(c => Math.round(c * scale));
}

/* ------------------------------------------------------------------- units */

/**
 * **Position is the wire format.** These numbers are the sprite index, the art
 * table key and the byte on the socket all at once, so nothing may be inserted
 * in the middle — a new unit goes on the end.
 *
 * They are left implicit deliberately. The three burglars were removed from the
 * middle of this list, which renumbered everything after them; rewriting twenty
 * literals by hand invites a duplicate value, and TypeScript accepts duplicate
 * enum values silently. Counting is the compiler's job.
 */
export enum UnitType {
  /* humans */
  Knight,
  Archer,
  Wizard,
  Dragon,
  /* civilians, shared by everyone */
  Caravan,
  Boat,
  /* dwarves */
  Shieldbearer,
  Crossbowman,
  Runesmith,
  Golem,
  /* elves */
  Blademaster,
  Longbow,
  Druid,
  Phoenix,
  /* monster-camp creatures — never trainable by players */
  Orc,
  Goblin,
  Ogre,
  Murloc,
  Wyvern,
  Griffin,
  /** The merchantman. A civilian like the caravan, appended rather than filed
   * beside the transport, because a unit added in the middle renumbers the
   * whole bestiary. */
  TradeShip,
  /**
   * **The warship** — the only thing in the game that fights at sea.
   *
   * One entry, not three. Every people sails the same hull with the same
   * numbers — what differs is the shape of it on the water (`shipArtFor`),
   * because a dwarven ironclad and an elven swan-prowed cutter being the same
   * ship underneath is a *drawing* decision, and a second set of statistics
   * would be a balance surface nobody asked for.
   */
  Warship,
  /** Always last: how many there are. Never a unit, never on the wire. */
  Count,
}
export const UNIT_COUNT = UnitType.Count;

/**
 * Civilians: they never fight, never count against the army cap, and are never
 * given marching orders. Written once here because the rule was previously
 * spelled out as a pair of comparisons in nine different files, and adding a
 * third civilian meant finding all nine.
 */
export const isCivilianUnit = (t: UnitType): boolean =>
  t === UnitType.Caravan || t === UnitType.Boat || t === UnitType.TradeShip;

/** Hulls. They move over water and only over water. */
export const isSeagoingUnit = (t: UnitType): boolean =>
  t === UnitType.Boat || t === UnitType.TradeShip || t === UnitType.Warship;

/**
 * **Can this soldier hurt anything at all?** — men, buildings *or* stonework.
 *
 * A civilian is not a soldier and carries nothing. The Shieldbearer is a
 * soldier that draws no blood: `damage` 0, so it cannot touch a man or a
 * building — but `siege`, so it can lean on a rampart until the rampart gives.
 *
 * Anything meaning "will this thing march, and should it look for something to
 * do?" asks here. Anything meaning "can it win a fight?" asks
 * `canStrikeLiving`. Both read the same table, so a second unit of either kind
 * will need no new code.
 */
export const isArmedUnit = (t: UnitType): boolean =>
  !isCivilianUnit(t) && (UNIT_DEFS[t].damage > 0 || (UNIT_DEFS[t].siege ?? 0) > 0);

/** Can it hurt a man or a building? A siege-only soldier cannot. */
export const canStrikeLiving = (t: UnitType): boolean =>
  !isCivilianUnit(t) && UNIT_DEFS[t].damage > 0;

/**
 * **What a soldier bought with mana does to a wall.**
 *
 * Wizards, Runesmiths, Druids, Shadowdancers, Dragons, Battle Golems and
 * Phoenixes are the only units in the game with mana in their price, and mana
 * has exactly one source — a Ley Shrine, on a ley field, which is a piece of
 * ground you have to go and take. Everything else can be raised from dirt.
 *
 * So this is the answer to the wall, and it is deliberately a *large* one. A
 * rampart is 620 HP and only soldiers can bring one down; a realm that has
 * turtled behind two thousand of them cannot be opened by ordinary infantry in
 * any reasonable time, which is the exact shape of every stalemate this world
 * has produced. Sorcery triples it — a Runesmith is worth six Crossbowmen
 * against stonework — so the counter to a fortress is to go and hold the ley
 * fields, and the counter to *that* is to take them off him.
 */
export const MANA_SIEGE_BONUS = 3;

/** Its strength against stonework — the only thing a Shieldbearer can break. */
export const siegePower = (def: UnitDef): number => {
  const base = def.damage > 0 ? def.damage : (def.siege ?? 0);
  return def.cost[Res.Mana] > 0 ? base * MANA_SIEGE_BONUS : base;
};

export interface UnitDef {
  key: string;
  name: string;
  hotkey: string;
  hp: number;
  damage: number;
  /**
   * Strength against **stonework only**, for a soldier whose `damage` is 0.
   *
   * Declared by exactly one unit. Everyone who can fight breaks a wall with the
   * same `damage` they hit everything else with; the Shieldbearer is the one
   * that can put its shoulder to a rampart and nothing else. Read it through
   * `siegePower`, never directly, so the ordinary case stays one rule.
   */
  siege?: number;
  attackInterval: number;
  range: number;
  aggroRange: number;
  splash: number;
  speed: number;
  cost: number[];          // [food, wood, stone, mana, gold]
  buildTime: number;
  flying: boolean;
  capture: number;
  raze: number;
  radius: number;
  from: StructureType[];
  trainable: boolean;
  /** tile control: how much this unit holds ground / softens ground */
  defencePower: number;
  assaultPower: number;
  /** heals the weakest ally in range instead of dealing damage */
  heals?: boolean;
  /** comes back once at half health when killed */
  rebirth?: boolean;
  desc: string;
}

/**
 * Tile control: units defend the ground they hold and soften the ground they
 * invade, out to `UNIT_INFLUENCE_RANGE` tiles.
 *
 * **Only the strongest unit in range counts — this does not stack.** The effect
 * used to be the *sum* of every unit standing nearby, which made piling twenty
 * soldiers onto one border tile strictly better than spreading them along the
 * front, and turned a doom-stack into the answer to every question. Taking the
 * best single unit instead means a Runesmith is worth what a Runesmith is worth,
 * whether he stands alone or in a crowd, and an army covers more border by
 * spreading out — which is also what an army should look like.
 */
export const UNIT_INFLUENCE_RANGE = 3;
/**
 * **A soldier standing on a tile roughly doubles what it costs to take.**
 *
 * This used to be 16 % per point of holding power, which was decorative: an
 * attack of ten thousand men did not care about a 16 % surcharge, so units were
 * a strike force and nothing else, and *where you put your army* had no bearing
 * on whether you kept your land. At 100 % per point a single Knight (1.0) makes
 * a tile twice as dear, a Shieldbearer (2.2) more than three times, a Runesmith
 * (3.4) four and a half. Positioning is now the strongest defensive tool in the
 * game, and it is the one that requires attention rather than resources.
 */
/*
 * Raised from 1.0 to 3.0: **a single soldier now quarters an attack.**
 *
 * Doubling was not enough to change anybody's behaviour. An assault of ten
 * thousand men shrugged off a 100 % surcharge on a handful of tiles, so the
 * army still went where the fighting was and the frontier stayed a line on a
 * map rather than something you manned. At 300 % per point a lone Knight makes
 * his tile four times as dear — the attack that would have rolled through him
 * takes a quarter as much ground — and a Shieldbearer or a Runesmith makes it
 * nearly unpassable. Standing troops on a border is now a real strategy with a
 * real cost, which is what it should have been all along.
 */
export const UNIT_DEFENCE_PER_POWER = 3.0;
export const UNIT_DEFENCE_CAP = 7.0;
/*
 * Raised alongside the defensive side, and for the same reason.
 *
 * A lone soldier now quarters an attack, which is what makes manning a border
 * worth doing — but if only the *defender* got stronger, war would simply stop
 * working: the first measurement after that change had a hundred-kingdom match
 * running 48 minutes instead of 25, because every front had become a wall
 * neither side could push. The answer is not to weaken the defence the player
 * asked for; it is to give the attacker the same lever. Bring your own troops
 * and the ground softens back toward what it was.
 *
 * So the shape of a war is now: unmanned borders fall to a mob, manned ones
 * need an army of your own. Which is the shape it should always have had.
 */
export const UNIT_ASSAULT_PER_POWER = 0.36;
export const UNIT_ASSAULT_CAP = 0.78;

/** Position is the index into `STRUCTURE_DEFS` and the byte on the wire — see
 * the note on `UnitType`. Append, never insert. */
export enum StructureType {
  Keep,
  /* economy, shared by every race */
  Farm,
  LumberCamp,
  Quarry,
  LeyShrine,
  Village,
  Watchtower,
  Market,
  Harbour,
  /* human military */
  Barracks,
  MageTower,
  DragonRoost,
  /* dwarven military */
  ForgeHall,
  RuneHall,
  GolemFoundry,
  /* elven military */
  BowyerGlade,
  MoonGrove,
  PhoenixAerie,
  /* monster camps — never buildable by players */
  OrcCamp,
  GoblinWarren,
  MurlocLagoon,
  DragonNest,
  GriffinNest,
  Warehouse,
  /** Always last: how many there are. Never a building. */
  Count,
}
export const STRUCTURE_COUNT = StructureType.Count;

/**
 * How much of each resource a realm can hold before it starts wasting income —
 * and how much more each Warehouse level buys. The ceiling exists so hoarding
 * is never the optimal play; the Warehouse exists so it is your decision how
 * high it sits.
 */
export const RES_CAP_BASE = 3000;

/**
 * **The number no stockpile may ever pass, whatever the edition says.**
 *
 * A safety rail, not a balance dial. A Light realm's ceiling is *uncapped* —
 * `Player.resCap` returned `Infinity` — which is fine as game design and is not
 * fine as arithmetic: `Infinity` propagates through every sum that touches it,
 * a number that large stops being exactly representable, and it goes on the
 * wire as a float somebody has to render. This is high enough that no realm in
 * any match will reach it, and finite enough that nothing downstream has to
 * cope with a value that is not a number.
 */
export const RES_HARD_CAP = 9_999_999;
/**
 * **A Warehouse level doubles the base ceiling: 3 000, the same as the realm
 * starts with.**
 *
 * It was half that, which quietly made the endgame unreachable. A race power
 * costs twelve thousand of each resource, so at 1 500 a level you needed six
 * levels of storage before you could so much as hold the price of one — and
 * thirteen before you could hold two. The building that is supposed to *unlock*
 * the late game was instead the thing standing in front of it.
 *
 * At 3 000 a level the maths is something a player can do in their head: the
 * first Warehouse doubles what you can keep, and three levels put a doomsday
 * weapon within reach.
 */
export const WAREHOUSE_CAP = 3000;

/**
 * **Soldiers are paid in food and gold. Never in timber or stone.**
 *
 * Wood and stone are what you *build* with — a farm, a wall, a harbour — and
 * gold and food are what you *run a country* on. Mixing the two meant every
 * army order competed with every building order for the same two piles, so the
 * player who wanted a barracks and the player who wanted soldiers were bidding
 * against themselves, and a realm on poor forest could not field infantry at
 * all. Now the two halves of the economy have their own currencies: the ground
 * you settle decides what you can *build*, and how many mouths you can feed
 * decides what you can *field*.
 *
 * The prices were not cut when the columns were merged — the timber and stone a
 * unit used to cost were folded into its food and gold, so an army costs what an
 * army always cost. Mana is untouched, because mana is not an economy: it comes
 * from one building on one kind of ground, and being unable to raise a Dragon
 * without ley fields is the whole point of it.
 */
export const UNIT_DEFS: UnitDef[] = [
  /* ------------------------------------------------------------- humans */
  {
    key: 'knight', name: 'Knight', hotkey: 'Q',
    hp: 140, damage: 16, attackInterval: 1.0, range: 0.9, aggroRange: 6, splash: 0,
    speed: 2.6, cost: [72, 0, 0, 0, 53], buildTime: 5, flying: false,
    capture: 1.0, raze: 0, radius: 0.55, from: [StructureType.Barracks], trainable: true,
    defencePower: 1.0, assaultPower: 1.0,
    desc: 'Captures enemy land by walking over it. The spearhead of every deep strike.',
  },
  {
    key: 'archer', name: 'Archer', hotkey: 'E',
    hp: 80, damage: 11, attackInterval: 0.85, range: 6, aggroRange: 8, splash: 0,
    speed: 2.4, cost: [60, 0, 0, 0, 40], buildTime: 4, flying: false,
    capture: 0, raze: 0, radius: 0.5, from: [StructureType.Barracks], trainable: true,
    defencePower: 1.1, assaultPower: 0.6,
    desc: 'Cheap ranged defender. Outranges knights, melts in melee.',
  },
  {
    key: 'wizard', name: 'Wizard', hotkey: 'R',
    hp: 70, damage: 22, attackInterval: 2.0, range: 7.5, aggroRange: 9, splash: 1.6,
    speed: 2.0, cost: [40, 0, 0, 50, 70], buildTime: 7, flying: false,
    capture: 0, raze: 0, radius: 0.5, from: [StructureType.MageTower], trainable: true,
    defencePower: 1.2, assaultPower: 1.2,
    desc: 'Splash damage, buffs allied units nearby (+15 % damage). Casts Meteor (F).',
  },
  {
    key: 'dragon', name: 'Dragon', hotkey: 'T',
    hp: 520, damage: 40, attackInterval: 1.4, range: 4, aggroRange: 8, splash: 2,
    speed: 3.2, cost: [240, 0, 0, 150, 420], buildTime: 18, flying: true,
    capture: 0, raze: 1.6, radius: 1.3, from: [StructureType.DragonRoost], trainable: true,
    defencePower: 3.0, assaultPower: 3.5,
    desc: 'Flies over ocean and mountains, razes territory back to neutral. Fund one together.',
  },
  /* ---------------------------------------------------------- civilians */
  {
    /*
     * **A wagon on a road, not an ox in a field.**
     *
     * At 2.2 a caravan was slower than every soldier in the game bar a Wizard,
     * which made a trade route feel less like traffic and more like a queue: a
     * long road was two minutes of watching a cart inch across it, and a realm
     * that built its markets far apart was punished for the geography rather
     * than rewarded for the risk. At 3.0 it is genuinely quick — quicker than a
     * Knight, level with a Transport.
     *
     * **It is still catchable, which is the number that actually mattered.**
     * Ground speed is multiplied by whose land is underneath (see
     * `TERRITORY_SPEED_ENEMY`), and a wagon crossing somebody's province does it
     * at 0.6 — an effective 1.80 — while the men who live there move over their
     * own fields at 1.0. A Knight at 2.6 still runs one down in his own country,
     * and a Blademaster, a Goblin, a Dragon, a Phoenix or a Griffin catches one
     * anywhere. Raiding a trade route is no harder than it was.
     *
     * **And it earns no more per minute than it used to.** A realm may only have
     * as many wagons on the road as it has market levels, so its trade income is
     * carts-in-flight divided by round-trip time — and round-trip time is
     * inversely proportional to this number. The freight came down by the same
     * factor the wagon sped up; see `CARAVAN_LOAD_SCALE`.
     */
    key: 'caravan', name: 'Caravan', hotkey: '',
    hp: 110, damage: 0, attackInterval: 99, range: 0, aggroRange: 0, splash: 0,
    speed: 3.0, cost: [0, 0, 0, 0, 0], buildTime: 0, flying: false,
    capture: 0, raze: 0, radius: 0.6, from: [StructureType.Market], trainable: false,
    defencePower: 0, assaultPower: 0,
    desc: 'Sent automatically between markets. Quick on the road, slow across foreign ground — and '
      + 'killing one pays the raider a quarter of its load.',
  },
  {
    key: 'boat', name: 'Transport', hotkey: '',
    hp: 260, damage: 0, attackInterval: 99, range: 0, aggroRange: 0, splash: 0,
    speed: 3.0, cost: [0, 0, 0, 0, 0], buildTime: 0, flying: false,
    capture: 0, raze: 0, radius: 0.8, from: [StructureType.Harbour], trainable: false,
    defencePower: 0, assaultPower: 0,
    desc: 'Carries troops across water from a Harbour and lands them as an invasion.',
  },
  /* ------------------------------------------------------------ dwarves */
  {
    /*
     * **A shield, and nothing else.**
     *
     * `damage: 0` is not a typo and not a nerf by half-measures — the
     * Shieldbearer draws no blood at all. It cannot kill a man, burn a farm or
     * put a pick into a rampart; a lone Shieldbearer left in front of an enemy
     * army will stand there and be cut down without landing a blow.
     *
     * It was the strongest unit in the game and the cheapest, both at once.
     * Holding power 2.2 against a knight's 1.0 makes a dwarf border tile two
     * and a third times dearer than a human's, from a tier-1 soldier six
     * seconds out of the Forge Hall — humans and elves need Dragons and
     * Phoenixes to reach the same ground. That was defensible on its own; what
     * was not is that the same unit also fought as well as a knight did. Now the
     * trade is honest: the dwarves field the best wall in the world and pay for
     * it by fielding a wall, and every dwarf army that wants to *kill* something
     * has to be Crossbowmen, Runesmiths and Golems doing the killing.
     *
     * **`siege` is the one thing it can still break, and it is not a
     * concession.** Stripped of that too, it was measured on the hundred-kingdom
     * world: a third of all realms are dwarves, so a third of the map lost its
     * cheap wall-breaking infantry, standing ramparts went from ~980 to 2 395,
     * and no war on the map could be finished at all. A shield against a
     * rampart is the one blow this unit has always been shaped for — it never
     * needed a sword to lean on a wall — and it changes nothing about the duel
     * it will still lose to a goblin.
     *
     * **The health went up with the sword coming off** — 280 to 520, level with
     * a Dragon and second only to a Battle Golem. Disarmed and left at 280 it
     * would have been a unit with no use in a battle at all; at 520 it has a
     * job, and it is the oldest job on a battlefield. It walks in front, the
     * enemy spends its arrows on the thing that cannot shoot back, and the
     * Crossbowmen behind it do the killing. Soaking damage *is* the weapon.
     *
     * `UNIT_DEFS` is the only place this is written down. `acquireTarget` will
     * not offer a siege-only soldier a man or a building to swing at — see
     * `units.ts` — so the only thing it ever walks toward is masonry.
     */
    key: 'shieldbearer', name: 'Shieldbearer', hotkey: 'Q',
    hp: 520, damage: 0, siege: 14, attackInterval: 1.2, range: 0.9, aggroRange: 5, splash: 0,
    speed: 1.9, cost: [80, 0, 0, 0, 60], buildTime: 6, flying: false,
    capture: 0.7, raze: 0, radius: 0.6, from: [StructureType.ForgeHall], trainable: true,
    defencePower: 2.2, assaultPower: 0.8,
    desc: 'A wall that walks — and only a wall. It strikes no blow at all: no soldier, no building, '
      + 'no rampart. What it does is soak what was meant for somebody else, take ground, and make '
      + 'the earth under it the dearest in the world to buy. Send it in front.',
  },
  {
    key: 'crossbow', name: 'Crossbowman', hotkey: 'E',
    hp: 95, damage: 24, attackInterval: 1.6, range: 5, aggroRange: 7, splash: 0,
    speed: 1.9, cost: [67, 0, 0, 0, 48], buildTime: 5, flying: false,
    capture: 0, raze: 0, radius: 0.5, from: [StructureType.ForgeHall], trainable: true,
    defencePower: 1.3, assaultPower: 0.7,
    desc: 'Slow, heavy bolts. Fewer shots than a bow, but each one lands like a hammer.',
  },
  {
    key: 'runesmith', name: 'Runesmith', hotkey: 'R',
    hp: 120, damage: 12, attackInterval: 1.6, range: 4, aggroRange: 6, splash: 0,
    speed: 2.0, cost: [60, 0, 0, 45, 85], buildTime: 7, flying: false,
    capture: 0, raze: 0, radius: 0.5, from: [StructureType.RuneHall], trainable: true,
    defencePower: 3.4, assaultPower: 0.3,
    desc: 'Carves wards into the ground. Land he stands on is brutally expensive to take.',
  },
  {
    key: 'golem', name: 'Battle Golem', hotkey: 'T',
    hp: 900, damage: 52, attackInterval: 2.0, range: 1.6, aggroRange: 6, splash: 1.8,
    speed: 1.5, cost: [280, 0, 0, 120, 460], buildTime: 20, flying: false,
    capture: 0.5, raze: 1.2, radius: 1.2, from: [StructureType.GolemFoundry], trainable: true,
    defencePower: 4.0, assaultPower: 3.0,
    desc: 'Stone given orders. It cannot fly and it cannot hurry, but nothing on the ground stops it.',
  },
  /* --------------------------------------------------------------- elves */
  {
    key: 'blademaster', name: 'Blademaster', hotkey: 'Q',
    hp: 105, damage: 19, attackInterval: 0.8, range: 0.9, aggroRange: 7, splash: 0,
    speed: 3.4, cost: [72, 0, 0, 0, 58], buildTime: 5, flying: false,
    capture: 1.4, raze: 0, radius: 0.5, from: [StructureType.BowyerGlade], trainable: true,
    defencePower: 0.7, assaultPower: 1.7,
    desc: 'Takes ground faster than anything else alive, and dies just as fast if it is caught.',
  },
  {
    key: 'longbow', name: 'Longbow', hotkey: 'E',
    hp: 65, damage: 13, attackInterval: 0.9, range: 8.5, aggroRange: 10, splash: 0,
    speed: 2.8, cost: [62, 0, 0, 0, 48], buildTime: 4, flying: false,
    capture: 0, raze: 0, radius: 0.5, from: [StructureType.BowyerGlade], trainable: true,
    defencePower: 1.2, assaultPower: 0.9,
    desc: 'The longest reach on the map. Kill it before it sees you, or do not bother.',
  },
  {
    key: 'druid', name: 'Druid', hotkey: 'R',
    hp: 90, damage: 16, attackInterval: 1.4, range: 6, aggroRange: 8, splash: 0,
    speed: 2.6, cost: [45, 0, 0, 50, 75], buildTime: 7, flying: false,
    capture: 0, raze: 0, radius: 0.5, from: [StructureType.MoonGrove], trainable: true,
    defencePower: 1.4, assaultPower: 1.0, heals: true,
    desc: 'Mends the wounded instead of burning the enemy. An elven army that lives is worth two that trade.',
  },
  {
    key: 'phoenix', name: 'Phoenix', hotkey: 'T',
    hp: 380, damage: 34, attackInterval: 1.2, range: 3.5, aggroRange: 9, splash: 1.8,
    speed: 4.2, cost: [200, 0, 0, 170, 360], buildTime: 18, flying: true,
    capture: 0, raze: 1.2, radius: 1.2, from: [StructureType.PhoenixAerie], trainable: true,
    defencePower: 2.4, assaultPower: 3.2, rebirth: true,
    desc: 'Faster than any dragon and it does not die the first time: it rises again at half strength.',
  },
  /* ---------------------------------------------------- camp creatures */
  {
    key: 'orc', name: 'Orc Raider', hotkey: '',
    hp: 160, damage: 15, attackInterval: 1.1, range: 0.9, aggroRange: 7, splash: 0,
    speed: 2.3, cost: [0, 0, 0, 0, 0], buildTime: 9, flying: false,
    capture: 0, raze: 0.4, radius: 0.6, from: [StructureType.OrcCamp], trainable: false,
    defencePower: 1.0, assaultPower: 1.0,
    desc: 'Camp creature. Burns whatever it walks over and never negotiates.',
  },
  {
    key: 'goblin', name: 'Goblin', hotkey: '',
    hp: 70, damage: 8, attackInterval: 0.7, range: 0.8, aggroRange: 8, splash: 0,
    speed: 3.4, cost: [0, 0, 0, 0, 0], buildTime: 5, flying: false,
    capture: 0, raze: 0, radius: 0.45, from: [StructureType.GoblinWarren], trainable: false,
    defencePower: 0.4, assaultPower: 0.4,
    desc: 'Camp creature. Fast, fragile, and always in numbers.',
  },
  {
    key: 'ogre', name: 'Ogre', hotkey: '',
    hp: 420, damage: 34, attackInterval: 1.8, range: 1.1, aggroRange: 6, splash: 1.2,
    speed: 1.8, cost: [0, 0, 0, 0, 0], buildTime: 26, flying: false,
    capture: 0, raze: 0.8, radius: 0.9, from: [StructureType.OrcCamp], trainable: false,
    defencePower: 2.2, assaultPower: 2.2,
    desc: 'Camp creature. Slow, enormous, and very bad news for a border town.',
  },
  {
    key: 'murloc', name: 'Murloc', hotkey: '',
    hp: 95, damage: 10, attackInterval: 0.9, range: 0.9, aggroRange: 7, splash: 0,
    speed: 2.9, cost: [0, 0, 0, 0, 0], buildTime: 6, flying: false,
    capture: 0, raze: 0, radius: 0.5, from: [StructureType.MurlocLagoon], trainable: false,
    defencePower: 0.6, assaultPower: 0.6,
    desc: 'Coastal creature. Swarms out of the shallows and drags things back in.',
  },
  {
    key: 'wyvern', name: 'Wyvern', hotkey: '',
    hp: 340, damage: 30, attackInterval: 1.5, range: 3.5, aggroRange: 9, splash: 1.5,
    speed: 3.4, cost: [0, 0, 0, 0, 0], buildTime: 30, flying: true,
    capture: 0, raze: 1.0, radius: 1.1, from: [StructureType.DragonNest], trainable: false,
    defencePower: 2.6, assaultPower: 2.6,
    desc: 'Wild dragonkin. Flies over anything and burns what it finds.',
  },
  {
    key: 'griffin', name: 'Griffin', hotkey: '',
    hp: 220, damage: 20, attackInterval: 1.1, range: 1.2, aggroRange: 9, splash: 0,
    speed: 4.0, cost: [0, 0, 0, 0, 0], buildTime: 16, flying: true,
    capture: 0, raze: 0, radius: 0.9, from: [StructureType.GriffinNest], trainable: false,
    defencePower: 1.8, assaultPower: 1.8,
    desc: 'Wild beast of the peaks. Very fast, and it hunts whatever crosses its territory.',
  },
  /* ------------------------------------------------ civilians, by sea */
  {
    key: 'tradeship', name: 'Merchantman', hotkey: '',
    hp: 200, damage: 0, attackInterval: 99, range: 0, aggroRange: 0, splash: 0,
    speed: 3.2, cost: [0, 0, 0, 0, 0], buildTime: 0, flying: false,
    capture: 0, raze: 0, radius: 0.8, from: [StructureType.Harbour], trainable: false,
    defencePower: 0, assaultPower: 0,
    desc: 'Sent automatically between harbours. Carries gold over water — and sinks with it.',
  },
  /* ------------------------------------------------------- war, by sea */
  {
    /**
     * **The warship.** Ordered from a Harbour by right-clicking open water.
     *
     * It is an ordinary soldier in every respect but two: it floats, and it
     * **takes no ground** (`capture: 0`). A navy cannot paint the map — the sea
     * belongs to nobody and always will — so what it is for is everything that
     * happens *on* the water: sinking transports before they land, cutting a
     * merchant route, and pulling down the Harbour those hulls came out of.
     *
     * `raze` rather than capture is the whole design. A coastal realm that has
     * never been reachable by land is reachable now: burn its port and its
     * fleet stops existing, its sea trade stops paying, and its invasions stop
     * arriving. That is a real answer to a turtle across the water, and it
     * needs no new rule — `raze` is the same number a Dragon uses.
     *
     * Priced in wood and gold and no mana at all: a navy is carpentry, not
     * sorcery, and every people can afford one from the first Harbour.
     */
    key: 'warship', name: 'Warship', hotkey: '',
    /*
     * **The longest reach in the game, and it has to be.**
     *
     * A hull cannot take a step inland, so every fight it has is fought from the
     * water at whatever distance the coastline dictates. Give it an archer's
     * reach and the shore simply wins: a Longbow standing on the beach (8.5)
     * shoots a warship that cannot shoot back and cannot close, and a navy
     * becomes a thing you build to watch it sink. Ten tiles puts the ship a
     * clear tile and a half past the furthest bow on the map — enough that
     * standing off a defended coast is a real position rather than a mistake,
     * and no more, because a fleet that outranged the land by a mile would make
     * every coastal province indefensible.
     */
    hp: 420, damage: 26, attackInterval: 1.1, range: 10, aggroRange: 12, splash: 0,
    /*
     * **Timber and coin, not rations** — the one exception to "soldiers are paid
     * in food and gold".
     *
     * Every other unit on this table is a person: you feed them and you pay
     * them. A warship is a *thing*, and the thing is made of wood. Pricing a
     * hull in grain read as an accounting quirk rather than as a decision, and
     * it left the timber economy with nothing to buy once the buildings were up.
     * The number is the same 115 that used to be food, moved a column across, so
     * this changes what a navy competes with and not what it costs.
     */
    speed: 3.4, cost: [0, 115, 0, 0, 255], buildTime: 12, flying: false,
    /*
     * `trainable: false` although you plainly do train it — because that flag
     * means "appears in a hall's training palette", and this one is laid down
     * by pointing at the sea. Saying otherwise would also put the Harbour on
     * the list of halls, and a hall is exactly what the **Light** edition bans
     * everything but the Barracks of: the harbour, sea trade and transports
     * would all vanish from Light to add a ship that was supposed to be in it.
     */
    /*
     * `capture` **and** `raze` both zero: this hull takes no ground and scrubs
     * none. `raze` fires on the tile a unit is standing on, and a warship only
     * ever stands on water — so a non-zero value here would either do nothing
     * at all or, the day something puts a hull over a shore tile, quietly start
     * eating a coastline the ship was never meant to touch.
     */
    capture: 0, raze: 0, radius: 0.9, from: [StructureType.Harbour], trainable: false,
    defencePower: 1.6, assaultPower: 1.6,
    desc: 'War at sea. Right-click open water with a Harbour to lay one down. Sinks transports, '
      + 'merchantmen and other warships, and burns Harbours — but takes no ground.',
  },
];

/**
 * How big an army you may field at once.
 *
 * This is **not** a flat number: it is what your military halls can support.
 * A second Barracks has to mean a bigger army, or there is no reason to raise
 * one. Every building that trains anything contributes `1 + its level`, on top
 * of a small standing retinue. Caravans and transports are civilians and never
 * count against it.
 */
/**
 * **Halls do not add to the army — they multiply each other.**
 *
 * Every level of a hall gives `2 + n`, where `n` is how many hall levels the
 * realm holds in total. So the first is worth three, and building a second
 * makes *both* worth four:
 *
 * | hall levels | each gives | army cap |
 * |---|---|---|
 * | 1 | 3 | 3 |
 * | 2 | 4 | 8 |
 * | 3 | 5 | 15 |
 * | 4 | 6 | 24 |
 *
 * `n × (n + 2)`, which is quadratic — the same shape as the yield curve on the
 * raw trades, and for the same reason. A flat `1 + level` per hall meant the
 * fifth Barracks was worth exactly what the first was while costing sixty
 * times as much, so nobody built it. Now committing to an army is a decision
 * that pays for itself, and a realm with one barracks and a big treasury is
 * genuinely different from one that built the barracks.
 *
 * **The Harbour is excluded.** It trains Warships, so it counts as a hall by
 * the old test, but a navy is not the field army and a fishing port should not
 * decide how many knights a realm can put on the ground.
 */
/**
 * **A standing retinue, for a realm with no halls at all.**
 *
 * The quadratic below already gives three at one hall level, so this changes
 * nothing about the progression that was asked for — it only answers the
 * question the progression does not: what a kingdom that has built nothing may
 * field. Zero is the wrong answer. A realm whose barracks was just burnt down
 * would have every surviving soldier become unfieldable, and a great many
 * fixtures — and a beginner's first minute — assume a kingdom can hold *a*
 * soldier without a building first.
 */
export const ARMY_CAP_FLOOR = 3;
export const ARMY_CAP_PER_HALL = 2;
/**
 * The ceiling on the above. Raised from 80, which the quadratic reaches at
 * eight hall levels — early enough to flatten the curve while a player was
 * still investing in it, which is exactly the failure the curve replaced.
 */
export const ARMY_CAP_MAX = 400;

/**
 * Army cap from hall levels: each level gives `2 + n`, so the total is n(n+2),
 * never below the standing retinue and never above the ceiling.
 *
 * The floor is invisible in the progression that was specified — one hall level
 * gives three, which is exactly the floor — so it costs the design nothing and
 * buys the "no halls" case a sane answer.
 */
export function armyCapFromHalls(hallLevels: number): number {
  const n = Math.max(0, hallLevels);
  return Math.min(ARMY_CAP_MAX, Math.max(ARMY_CAP_FLOOR, n * (n + ARMY_CAP_PER_HALL)));
}
export const GLOBAL_UNIT_CAP = 1500;

/**
 * **Every price in the game is a round number.**
 *
 * Escalation multiplies, and multiplication produces prices like 176, 281 and
 * 451 — numbers a player cannot hold in their head, cannot compare at a glance,
 * and cannot do arithmetic with while a war is on. None of that precision buys
 * anything: the difference between 451 and 450 is not a balance decision, it is
 * a rounding artefact of the curve.
 *
 * So prices snap to a step that grows with the number, the way money is written
 * everywhere else — fives while it is small, then tens, fifties, five hundreds,
 * thousands. A price is always readable at the size it is actually seen at, and
 * two prices in the same bracket can be compared by looking at them.
 *
 * The floor matters: anything that costs *something* must never round to
 * nothing, or a rounding rule would quietly make a unit free.
 */
export function roundPrice(n: number): number {
  if (n <= 0) return 0;
  const step = n < 50 ? 5
    : n < 500 ? 10
      : n < 5000 ? 50
        : n < 50000 ? 500
          : 1000;
  return Math.max(step, Math.round(n / step) * step);
}

/**
 * **Every soldier of a kind you already have makes the next one dearer — and it
 * compounds, exactly as building prices do.**
 *
 * It used to be linear: `1 + 0.3n`, so the tenth knight cost 3.7 times the
 * first. That is not a brake, it is a mild tax — and it meant the answer to
 * "what should I spend a large treasury on" was always "more of whatever I
 * already have", because the twentieth of a kind was still nearly affordable.
 * Buildings learned this lesson already; see `BUILDING_COST_STEP`.
 *
 * At 1.3 compounding the fifth of a kind costs about three times the first and
 * the tenth about eleven times, which is where a real decision lives: a
 * specialist army of one unit becomes something you pay through the nose for,
 * and a mixed one is the cheap answer. The army cap (`armyCapFromHalls`) bounds
 * the total; this decides its *shape*.
 */
export const UNIT_COST_STEP = 1.3;

/**
 * One dial over every soldier's price, kept apart from the tables so those stay
 * a statement of relative worth. Raised because an army should be a serious
 * commitment against a realm's income rather than something bought out of
 * pocket change.
 */
export const UNIT_PRICE_SCALE = 1.5;

export function unitCost(def: UnitDef, owned: number): number[] {
  const scale = Math.pow(UNIT_COST_STEP, Math.max(0, owned)) * UNIT_PRICE_SCALE;
  return def.cost.map(c => roundPrice(c * scale));
}

/**
 * **What a soldier costs in people.**
 *
 * Your people *are* your army — the men in the troop bar are the same men who
 * live in the villages and pay the taxes — so a trained soldier is not
 * conjured, he is *taken out of that number*. Without this, a hall turned grain
 * and coin into fighting men from nowhere, and the population that the whole
 * economy is built around was untouched by the one thing that should draw on
 * it.
 *
 * Derived from the food price rather than tabled per unit, because food is
 * already the resource that means "people eating": whatever a soldier eats, a
 * quarter of that number is how many citizens he *is*. That keeps the two in
 * step automatically — a unit rebalanced in food is rebalanced in people — and
 * it escalates with the rest, so the tenth knight takes ten knights' worth of
 * countrymen with him.
 *
 * Anything free in food, like a caravan, costs no people either.
 */
export const UNIT_POP_PER_FOOD = 0.25;

export function unitPopCost(def: UnitDef, owned: number): number {
  const food = unitCost(def, owned)[Res.Food] || 0;
  return Math.round(food * UNIT_POP_PER_FOOD);
}
export const UNIT_ATTRITION = 0.5;
export const WIZARD_AURA_RANGE = 5;
export const WIZARD_AURA_BONUS = 0.15;
export const DRUID_HEAL = 14;                // hp restored per cast
export const REBIRTH_HP = 0.5;
/** Meteor is paid in mana — the wizard's own reserve of it. */
export const METEOR_COST = [0, 0, 0, 35, 20];
export const METEOR_DAMAGE = 90;
export const METEOR_RADIUS = 3;
export const METEOR_COOLDOWN = 12;

/**
 * Per-type army limits: each building that can train a unit supports
 * `1 + level` of it.
 */
export const UNITS_PER_BUILDING = 1;
export const UNITS_PER_BUILDING_LEVEL = 1;

/**
 * Units take up space: they push each other apart instead of stacking — but
 * **weight decides who moves**. A goblin walking into a Shieldbearer bounces off
 * it; a Battle Golem walking into the same Shieldbearer shoves it aside. Mass is
 * derived from health and footprint, so nothing needs a hand-tuned table.
 */
export const UNIT_SEPARATION = 0.9;
export const UNIT_SEPARATION_PUSH = 0.55;

export function unitMass(def: UnitDef): number {
  // health and footprint say how big it is; ground control says how planted it
  // is, which is exactly what a Shieldbearer is for
  return def.hp * (0.3 + def.radius) * (1 + def.defencePower);
}

/**
 * Ground units march on their own roads and slog through everyone else's.
 * Flying units ignore this entirely — and terrain.
 */
export const TERRITORY_SPEED_OWN = 1.0;
export const TERRITORY_SPEED_NEUTRAL = 0.85;
export const TERRITORY_SPEED_ENEMY = 0.6;

/* ------------------------------------------------------------- caravans */

export const CARAVAN_INTERVAL = 22;
export const CARAVAN_MAX_PER_MARKET = 2;
/**
 * The freight. **Measured, not guessed** — and the measurement said leave it.
 *
 * The return leg halves what a cart earns per minute, so doubling these looked
 * like the obvious correction. It is not: a 100-kingdom match with double
 * freight had a *fifth* of the map's leader at 38 minutes where the current
 * numbers had 94 %, because the extra gold goes into soldiers, soldiers quarter
 * an attack on the ground they hold (`UNIT_DEFENCE_PER_POWER`), and a hundred
 * realms that can all afford a garrison is a hundred realms nobody can push
 * through. Richer is not faster here; richer is a stalemate.
 *
 * Leave trade alone. If the war wants shortening, the lever is what an army
 * costs and what it is worth on defence, not what a wagon carries.
 *
 * That is a statement about gold **per minute**, and it is why speeding the
 * wagon up could not be done on its own: see `CARAVAN_LOAD_SCALE`, which takes
 * the cart's load down by exactly what the extra speed put back. These three
 * numbers are unchanged, and they are shared with the merchantman.
 */
/**
 * **Distance is now most of what a load is worth, and it pays a curve.**
 *
 * It used to be a flat base plus a per-tile rate, so the shortest legal trip
 * paid 82 gold and the longest 203 — two and a half times, across the whole
 * width of a continent. That made the sensible play "put two markets next to
 * each other and let the wagons shuttle": no exposure, no escort, no decision,
 * and the same gold per minute as a real trade route.
 *
 * So the base is now nearly nothing and the distance term is raised to a power.
 * Measured across the caravan's range, at level 1 to a foreign market:
 *
 *   20 tiles   82 -> 35     the shuttle is no longer worth the wagon
 *   40 tiles   96 -> 71
 *   80 tiles  124 -> 161    they cross over around 55 tiles
 *  120 tiles  163 -> 267
 *  170 tiles  203 -> 367    (`CARAVAN_MAX_GOLD` binds here and holds the top)
 *
 * A ten-fold spread instead of a two-and-a-half-fold one, which is the point:
 * the far market is now worth the road to it, and the road is where a caravan
 * can be raided. `CARAVAN_MAX_GOLD` is deliberately left where it was — it is
 * the rail that stops a level-5 market across a continent from printing money,
 * and raising it is a separate decision that wants its own measurement.
 */
export const CARAVAN_BASE_GOLD = 8 * GAME_PACE;
export const CARAVAN_DISTANCE_GOLD = 0.293 * GAME_PACE;
export const CARAVAN_DISTANCE_EXP = 1.335;
export const CARAVAN_MAX_GOLD = 400 * GAME_PACE;
export const CARAVAN_FOREIGN_BONUS = 1.6;

/**
 * What a load is worth before the cap, the hold bonus and the wagon's own
 * `CARAVAN_LOAD_SCALE` — **one formula, three callers**.
 *
 * The wagon dispatcher, the hull dispatcher and the merchant's own choice of
 * partner all used to spell this out separately. Three copies of an arithmetic
 * expression is three chances for the price a merchant *chases* to stop being
 * the price the treasury *pays*, and that particular drift is invisible: the
 * traders simply start preferring the wrong markets.
 */
export function tradeFreight(distance: number, foreign: boolean): number {
  const d = Math.max(0, distance);
  return (CARAVAN_BASE_GOLD + CARAVAN_DISTANCE_GOLD * Math.pow(d, CARAVAN_DISTANCE_EXP))
    * (foreign ? CARAVAN_FOREIGN_BONUS : 1);
}
/**
 * **What a faster wagon costs, so that speed buys convenience and not money.**
 *
 * The Caravan went from 2.2 to 3.0 — see its entry in `UNIT_DEFS`. Trade income
 * is not paid per wagon, it is paid per *arrival*, and a realm may only have as
 * many wagons on the road as it has market levels: gold per minute is therefore
 * carts-in-flight ÷ round-trip time, and round-trip time is inversely
 * proportional to speed. A wagon 36 % quicker is a treasury 36 % richer, and the
 * measurement above says plainly what a richer map does to a war.
 *
 * So the load comes down by exactly the factor the wagon gained: 2.2/3.0. Trade
 * pays what it always paid; it simply pays in more, smaller instalments, which
 * is what "the caravans are faster" ought to feel like. It also makes any single
 * cart a smaller prize, which is the honest counterweight to the fact that the
 * gold is now on the road for a shorter time and harder to intercept.
 *
 * **Caravans only.** `CARAVAN_BASE_GOLD` and its companions are shared with the
 * merchantman (see `SHIP_CARGO_BONUS`, which is the same idea pointed the other
 * way), and the hull did not get any faster, so it must not get any poorer.
 */
export const CARAVAN_LOAD_SCALE = 2.2 / 3.0;
/**
 * What the *host* market's realm earns from a caravan arriving.
 *
 * Paid **only to an ally**. Trade with somebody on your own side is a shared
 * enterprise and both treasuries grow; trade across the line is not charity —
 * see `CARAVAN_CROSS_TEAM_BONUS` for where the other realm's share goes when
 * the other realm is not a friend.
 */
export const CARAVAN_PARTNER_SHARE = 0.4;
/**
 * The premium for selling into a rival's market.
 *
 * A caravan that reaches a market outside your alliance pays its sender the
 * host's share **as well as** its own — plus this on top. Nothing at all goes
 * to the host. You are not developing their economy; you are extracting from
 * it, and the risk of the road is priced in: their soldiers are on it.
 */
export const CARAVAN_CROSS_TEAM_BONUS = 0.2;
/**
 * The premium for selling to another people.
 *
 * What a dwarf hall wants is not what an elf grove wants, and the merchant in
 * between is the one who profits from the difference. This replaced a flat
 * racial trade bonus that only humans had — and which, as it turned out, was
 * never wired to anything at all. Now the bonus is a *relationship* rather
 * than a birthright: whoever you are, trading with somebody unlike you pays.
 */
export const CARAVAN_CROSS_RACE_BONUS = 0.5;
/** Longer roads on a continent three times the size. */
export const CARAVAN_MAX_RANGE = 170;
export const CARAVAN_TIMEOUT = 190;

/* -------------------------------------------------------- trade shipping */

/**
 * **Harbours trade exactly the way markets do, only over water.**
 *
 * Same out-and-back journey, same payout rules, same realm-wide budget of one
 * hull per harbour level. The difference is the map: a wagon is stopped by the
 * first stretch of sea, and a ship is stopped by the first stretch of land —
 * so a harbour reaches the islands and the far coasts that no road will ever
 * touch, and a kingdom on a small island stops being a kingdom that cannot
 * trade.
 *
 * A hull carries more than a cart and goes further, which is the reason to
 * build one; it is also slower to come home and cannot be defended by the army
 * standing on your border, which is the reason not to rely on it.
 */
export const SHIP_INTERVAL = 26;
/** A sea lane can be far longer than a road, because nothing has to be crossed. */
export const SHIP_MAX_RANGE = 300;
/** What a hold carries, against a cart of the same trade. */
export const SHIP_CARGO_BONUS = 1.45;
export const SHIP_TIMEOUT = 300;

/* ------------------------------------------------------------------ boats */

export const BOAT_MIN_TROOPS = 20;
export const BOAT_TIMEOUT = 150;
export const BOAT_ABORT_REFUND = 0.5;

/* -------------------------------------------------------------- structures */

export interface StructureDef {
  key: string;
  name: string;
  hp: number;
  cost: number[];
  preferred: Terrain[] | null;
  /**
   * Favourite ground that depends on who is building it, indexed by `Race`.
   *
   * Villages are the one building where "the right place to live" is a fact
   * about the people, not about the trade: humans farm the open plains, elves
   * live in the forest, dwarves cut their halls into the mountain. Where this
   * is set it replaces `preferred` entirely; `preferred` stays as the answer
   * for anyone asking without naming a race.
   */
  preferredByRace?: Terrain[][];
  required?: Terrain[];        // cannot be built anywhere else at all
  buildable?: boolean;
  captureable?: boolean;
  coastal?: boolean;
  race?: Race;                 // undefined = every race may build it
  limit: number;
  income: number[];
  popCap: number;
  defenceBonus: number;
  defenceRadius: number;
  zapDamage: number;
  zapRange: number;
  troopCap: number;
  trains: UnitType[];
  desc: string;
}

/**
 * What a building is *for*, at a glance.
 *
 * Twenty-six buildings on one map, drawn at sixteen pixels, is more than anyone
 * can hold in their head — and the question a player actually asks is never
 * "which of the twenty-six is that", it is "is that thing making them money,
 * making them soldiers, or making them people". So every name carries a mark:
 *
 *   ○  economy   — it produces, stores or taxes
 *   △  military  — it trains, garrisons or shoots
 *   ⌂  housing   — it is where the people live
 *
 * Derived from what the building *does* rather than hand-listed, so a new
 * building is classified correctly the day it is added and cannot drift out of
 * step with its own stats.
 */
export enum BuildingKind { Economy = 0, Military = 1, Housing = 2 }

export function buildingKind(def: StructureDef): BuildingKind {
  // the Village is the one building whose product is people
  if (def.key === 'village') return BuildingKind.Housing;
  if (def.trains.length > 0 || def.zapDamage > 0 || def.troopCap > 0) return BuildingKind.Military;
  return BuildingKind.Economy;
}

/** The glyph key for each kind — see `ICONS` in the client's icon set. */
export const BUILDING_KIND_ICON = ['kind-econ', 'kind-mil', 'kind-home'];
export const BUILDING_KIND_NAME = ['economy', 'military', 'housing'];

/**
 * Buildings are raised, not conjured.
 *
 * A new building spends this long as a construction site: it shows scaffolding
 * and a filling bar, and produces nothing, trains nothing and projects no aura
 * until the last plank is on. **Levelling an existing building is instant** —
 * that is an extension to something already standing, and making the player
 * wait twice for the same plot would only punish investment.
 */
export const BUILD_SECONDS = 7;
export const BUILD_SECONDS_PER_COST = 0.007;   // grander projects take longer

export function buildSeconds(def: StructureDef): number {
  const total = def.cost.reduce((a, c) => a + c, 0);
  return (BUILD_SECONDS + total * BUILD_SECONDS_PER_COST) * GAME_PACE;
}

/**
 * What a building yields on ground it does not want.
 *
 * **60 %, not 20 %.** At a fifth, geography was not a preference but a
 * sentence: a kingdom that spawned with no forest inside its borders had no
 * way to make timber worth the name — its Lumber Camps were rounding error and
 * its only answer was to conquer a forest or lose. That is a map roll deciding
 * a match before anybody plays it.
 *
 * At 60 % the *right* ground is still plainly worth having — a Quarry in the
 * mountains is worth nearly two anywhere else — but a bad draw is a
 * disadvantage to work around rather than a wall. Where you build still
 * matters; it just no longer decides who can play.
 */
export const WRONG_TERRAIN_FACTOR = 0.6;
export const MAX_STRUCTURE_LEVEL = 5;
/**
 * What a level buys a building's auras: a stronger bonus over a wider circle,
 * and a longer shot for anything that shoots. Without the radius term a
 * Watchtower's levels were nearly invisible — a few percent of defence on
 * exactly the same tiles — and levelling one was a waste of stone.
 */
export const AURA_POWER_PER_LEVEL = 0.25;
export const AURA_RADIUS_PER_LEVEL = 0.18;
export const LEVEL_COST_SCALE = 1;
/**
 * **Each building of a kind costs 60 % more than the one before it — compounding.**
 *
 * This used to be a flat increment: the nth cost `1 + 0.65n` times the first, so
 * the twentieth was thirteen times the price of the first and an established
 * empire could simply buy twenty of everything. That is why hard caps existed
 * at all — the price was never a real brake, so ceilings had to be bolted on
 * top of it.
 *
 * Compounding makes the price the brake it was always described as. The fourth
 * building costs about what it did; the tenth costs sixty-eight times the
 * first, which no treasury reaches. So nothing needs a cap: you can build as
 * many houses as you can pay for, the answer is always "one more, if you can
 * afford it", and the game answers that question with arithmetic instead of a
 * rule nobody can see.
 */
export const BUILDING_COST_STEP = 1.6;

/**
 * What pulling a building down hands back, per level of it.
 *
 * A quarter of the price in **materials only — never gold**. Coin is minted by
 * trade and taxed out of people; nobody finds it in the rubble of a house, and
 * paying gold for demolition would make razing a neighbour's villages an income
 * stream instead of the cost it is meant to be.
 */
export const DEMOLITION_SALVAGE = 0.25;

/**
 * There is no ceiling on how much you may build.
 *
 * Every extra copy of a building costs 60 % more than the last, compounding
 * (`buildingCost`) — a far better brake than a hard cap, because it lets a rich
 * player keep investing while making the tenth farm a real decision instead of
 * a free one, and the eleventh one an absurd one. Only the capital and the
 * monster lairs stay singular.
 *
 * Declared here rather than below the table because `STRUCTURE_DEFS` now uses
 * it: a `const` referenced during the array literal's own evaluation would sit
 * in the temporal dead zone and throw at import time.
 */
export const UNLIMITED = 999;

export const STRUCTURE_DEFS: StructureDef[] = [
  {
    key: 'keep', name: 'Keep', hp: 800, cost: [0, 0, 0, 0, 0], preferred: null, limit: 1,
    income: [0, 0, 0, 0, 0], popCap: 80, defenceBonus: 0.25, defenceRadius: 8,
    zapDamage: 0, zapRange: 0, troopCap: 200, trains: [],
    desc: 'Your capital. Granted for free at spawn.',
  },
  /* ------------------------------------------------------------ economy */
  /*
   * **The three that feed and supply you, all doubled.**
   *
   * Raising these rather than starving the land is what makes developing worth
   * it without making geography a sentence: the ground gives every kingdom a
   * floor, and these are how a kingdom builds a ceiling. Doubled together so
   * the three stay in the same proportion to one another as they always were —
   * food is still the most plentiful, stone the scarcest.
   */
  {
    key: 'farm', name: 'Farm', hp: 200, cost: [0, 110, 0, 0, 30], preferred: [Terrain.Plains], limit: 4,
    income: [1.50, 0, 0, 0, 0], popCap: 0, defenceBonus: 0, defenceRadius: 0,
    zapDamage: 0, zapRange: 0, troopCap: 0, trains: [],
    desc: 'Food, and most of your food. Best on plains.',
  },
  {
    key: 'lumber', name: 'Lumber Camp', hp: 200, cost: [110, 0, 0, 0, 30], preferred: [Terrain.Forest], limit: 4,
    income: [0, 1.30, 0, 0, 0], popCap: 0, defenceBonus: 0, defenceRadius: 0,
    zapDamage: 0, zapRange: 0, troopCap: 0, trains: [],
    desc: 'Wood, and most of your wood. Best in forest — but it still pays well anywhere.',
  },
  {
    key: 'quarry', name: 'Quarry', hp: 240, cost: [0, 150, 0, 0, 40], preferred: [Terrain.Mountain], limit: 4,
    income: [0, 0, 1.10, 0, 0], popCap: 0, defenceBonus: 0, defenceRadius: 0,
    zapDamage: 0, zapRange: 0, troopCap: 0, trains: [],
    desc: 'Stone, and most of your stone. Best in the mountains — but it still pays well anywhere.',
  },
  {
    key: 'shrine', name: 'Ley Shrine', hp: 200, cost: [0, 90, 180, 0, 110],
    preferred: [Terrain.Ley], required: [Terrain.Ley], limit: 3,
    income: [0, 0, 0, 0.30, 0], popCap: 0, defenceBonus: 0.1, defenceRadius: 3,
    zapDamage: 0, zapRange: 0, troopCap: 0, trains: [],
    desc: 'The ONLY source of mana, and it can only stand on a ley field. Everything magical runs on these.',
  },
  {
    // the most expensive thing in the early game, because it is the strongest:
    // every level is worth a sixth of your whole population ceiling
    // a village pays *rent*, not a fortune: houses used to be the best gold
    // building in the game by accident, which made the market pointless
    // no limit: houses are what a population is made of, and telling a player
    // "you may not build a sixth" is a rule where a price belongs
    key: 'village', name: 'Village', hp: 260, cost: [120, 180, 60, 0, 90],
    preferred: [Terrain.Plains], limit: UNLIMITED,
    /*
     * A people build their homes where their people have always built them:
     * humans on the open plain, dwarves inside the mountain, elves under the
     * canopy. Same building, same price — but where it works at full strength
     * is a different map for each of you, and that quietly makes the ground you
     * fight over depend on who you are. Indexed by `Race`.
     */
    preferredByRace: [[Terrain.Plains], [Terrain.Mountain], [Terrain.Forest]],
    income: [0.1, 0, 0, 0, 0.01], popCap: POP_PER_VILLAGE_LEVEL, defenceBonus: 0.05, defenceRadius: 3,
    zapDamage: 0, zapRange: 0, troopCap: 0, trains: [],
    desc: 'Housing. Every level raises your population ceiling by a share of your whole realm and ' +
      'speeds recruitment. Your people have their own idea of home: humans want plains, dwarves ' +
      'mountains, elves forest. Pays a little rent — for real money, build a Market.',
  },
  {
    key: 'watchtower', name: 'Watchtower', hp: 300, cost: [0, 130, 90, 0, 40], preferred: null,
    captureable: false, limit: 4,
    income: [0, 0, 0, 0, 0], popCap: 0, defenceBonus: 0.3, defenceRadius: 6,
    // an archer tower: it shoots further than a mage tower and hits softer, and
    // the arrows are the point — a defensive building that cannot kill anything
    // only ever delays an attack it should be punishing
    zapDamage: 9, zapRange: 7, troopCap: 0, trains: [],
    desc: 'Looses arrows at any intruder within 7 and adds +30 % tile defence in radius 6. ' +
      'Burned down when its tile falls.',
  },
  {
    /*
     * The money building — but it makes its money out of **your people**, not
     * out of thin air. A flat, generous income of its own turned a Market into
     * a gold mine you parked in a corner and forgot; most of what it pays now
     * arrives as a rise in the head tax (`GOLD_TAX_PER_TRADE_LEVEL`), so a
     * market is worth exactly as much as the population it serves. Cheap to
     * raise, because a realm with no money cannot buy its way out of having no
     * money.
     */
    /*
     * **Paid for in timber and stone, never in coin.**
     *
     * A realm with no money cannot buy its way out of having no money, and the
     * Market is the building that fixes that — so charging gold for it was the
     * one price in the game that could lock a player out of the answer to their
     * own problem. The gold it used to cost is now timber and stone, which a
     * poor realm can cut and quarry out of ground it already holds.
     */
    key: 'market', name: 'Market', hp: 220, cost: [90, 190, 90, 0, 0], preferred: null, limit: 3,
    income: [0, 0, 0, 0, 0.16], popCap: 25, defenceBonus: 0, defenceRadius: 0,
    zapDamage: 0, zapRange: 0, troopCap: 0, trains: [],
    desc: 'Your treasury. Every level makes your whole population pay more tax, earns gold of its ' +
      'own on top, and sends caravans to other markets — the richer the further and the more foreign.',
  },
  {
    key: 'harbour', name: 'Harbour', hp: 300, cost: [0, 130, 70, 0, 60], preferred: null,
    coastal: true, limit: 3,
    // the other half of the money, and the same shape: port dues are a tax on
    // the people who trade through it, not a fountain in the harbour master's
    // garden. This is why a coastline is worth holding
    income: [0.1, 0, 0, 0, 0.10], popCap: 20, defenceBonus: 0.1, defenceRadius: 3,
    zapDamage: 0, zapRange: 0, troopCap: 0, trains: [],
    desc: 'Must stand beside water — the sea, the shallows or a river. Every level raises the tax ' +
      'your whole population pays and earns port dues of its own. It launches troop transports ' +
      'and warships from whatever water it stands on, so a river port can put an army on the ' +
      'far bank.',
  },
  /* ----------------------------------------------------- human military */
  {
    key: 'barracks', name: 'Barracks', hp: 320, cost: [0, 190, 110, 0, 90], preferred: null,
    race: Race.Human, limit: 3,
    income: [0, 0, 0, 0, 0], popCap: 0, defenceBonus: 0.1, defenceRadius: 3,
    zapDamage: 0, zapRange: 0, troopCap: 40, trains: [UnitType.Knight, UnitType.Archer],
    desc: 'Trains Knights and Archers.',
  },
  {
    key: 'tower', name: 'Mage Tower', hp: 300, cost: [0, 70, 190, 60, 190], preferred: [Terrain.Ley],
    race: Race.Human, limit: 2,
    income: [0, 0, 0, 0.05, 0], popCap: 0, defenceBonus: 0.15, defenceRadius: 4,
    zapDamage: 14, zapRange: 6, troopCap: 0, trains: [UnitType.Wizard],
    desc: 'Trains Wizards and zaps intruders. Strongest on ley fields.',
  },
  {
    key: 'roost', name: 'Dragon Roost', hp: 500, cost: [0, 260, 430, 170, 560],
    preferred: [Terrain.Mountain], race: Race.Human, limit: 1,
    income: [0, 0, 0, 0, 0], popCap: 0, defenceBonus: 0.1, defenceRadius: 4,
    zapDamage: 0, zapRange: 0, troopCap: 0, trains: [UnitType.Dragon],
    desc: 'Trains Dragons. Best in the mountains.',
  },
  /* ---------------------------------------------------- dwarven military */
  {
    key: 'forgehall', name: 'Forge Hall', hp: 420, cost: [0, 160, 160, 0, 90],
    preferred: [Terrain.Mountain], race: Race.Dwarf, limit: 3,
    income: [0, 0, 0.05, 0, 0], popCap: 0, defenceBonus: 0.15, defenceRadius: 3,
    zapDamage: 0, zapRange: 0, troopCap: 50, trains: [UnitType.Shieldbearer, UnitType.Crossbowman],
    desc: 'Trains Shieldbearers and Crossbowmen. Doubles as a bunker: +15 % defence around it.',
  },
  {
    key: 'runehall', name: 'Rune Hall', hp: 380, cost: [0, 70, 230, 60, 190],
    preferred: [Terrain.Ley], race: Race.Dwarf, limit: 2,
    income: [0, 0, 0, 0.05, 0], popCap: 0, defenceBonus: 0.25, defenceRadius: 5,
    zapDamage: 10, zapRange: 5, troopCap: 0, trains: [UnitType.Runesmith],
    desc: 'Trains Runesmiths. The wards make it the strongest defensive building in the game.',
  },
  {
    key: 'foundry', name: 'Golem Foundry', hp: 620, cost: [0, 220, 540, 140, 540],
    preferred: [Terrain.Mountain], race: Race.Dwarf, limit: 1,
    income: [0, 0, 0, 0, 0], popCap: 0, defenceBonus: 0.15, defenceRadius: 4,
    zapDamage: 0, zapRange: 0, troopCap: 0, trains: [UnitType.Golem],
    desc: 'Trains Battle Golems. Best in the mountains, where the stone is.',
  },
  /* ------------------------------------------------------ elven military */
  {
    key: 'bowyer', name: 'Bowyer Glade', hp: 260, cost: [0, 220, 60, 0, 90],
    preferred: [Terrain.Forest], race: Race.Elf, limit: 3,
    income: [0, 0.05, 0, 0, 0], popCap: 0, defenceBonus: 0.1, defenceRadius: 3,
    zapDamage: 0, zapRange: 0, troopCap: 35, trains: [UnitType.Blademaster, UnitType.Longbow],
    desc: 'Trains Blademasters and Longbows. Grows best in the forest.',
  },
  {
    key: 'moongrove', name: 'Moon Grove', hp: 280, cost: [0, 175, 60, 60, 190],
    preferred: [Terrain.Ley], race: Race.Elf, limit: 2,
    income: [0.1, 0, 0, 0.05, 0], popCap: 0, defenceBonus: 0.15, defenceRadius: 4,
    zapDamage: 12, zapRange: 6, troopCap: 0, trains: [UnitType.Druid],
    desc: 'Trains Druids. The grove itself mends the land it stands on.',
  },
  {
    key: 'aerie', name: 'Phoenix Aerie', hp: 460, cost: [0, 320, 230, 200, 540],
    preferred: [Terrain.Ley], race: Race.Elf, limit: 1,
    income: [0, 0, 0, 0, 0], popCap: 0, defenceBonus: 0.1, defenceRadius: 4,
    zapDamage: 0, zapRange: 0, troopCap: 0, trains: [UnitType.Phoenix],
    desc: 'Trains Phoenixes. Best on ley fields, where the fire remembers how to come back.',
  },
  /* ------------------------------------------------------- monster camps */
  {
    key: 'orccamp', name: 'Orc Camp', hp: 420, cost: [0, 0, 0, 0, 0], preferred: null,
    buildable: false, captureable: false, limit: 1,
    income: [0, 0, 0, 0, 0], popCap: 0, defenceBonus: 0.2, defenceRadius: 5,
    zapDamage: 0, zapRange: 0, troopCap: 0, trains: [UnitType.Orc, UnitType.Ogre],
    desc: 'A palisade of orcs. Breeds raiders and ogres, and answers to nobody.',
  },
  {
    key: 'goblincamp', name: 'Goblin Warren', hp: 300, cost: [0, 0, 0, 0, 0], preferred: null,
    buildable: false, captureable: false, limit: 1,
    income: [0, 0, 0, 0, 0], popCap: 0, defenceBonus: 0.15, defenceRadius: 4,
    zapDamage: 0, zapRange: 0, troopCap: 0, trains: [UnitType.Goblin],
    desc: 'A warren of goblins. Spills out fast, greedy raiding parties.',
  },
  {
    key: 'murloccamp', name: 'Murloc Lagoon', hp: 280, cost: [0, 0, 0, 0, 0], preferred: null,
    buildable: false, captureable: false, coastal: true, limit: 1,
    income: [0, 0, 0, 0, 0], popCap: 0, defenceBonus: 0.15, defenceRadius: 4,
    zapDamage: 0, zapRange: 0, troopCap: 0, trains: [UnitType.Murloc],
    desc: 'A reeking lagoon of murlocs. Always on a coast, always hungry.',
  },
  {
    key: 'dragonnest', name: 'Wyvern Nest', hp: 480, cost: [0, 0, 0, 0, 0], preferred: null,
    buildable: false, captureable: false, limit: 1,
    income: [0, 0, 0, 0, 0], popCap: 0, defenceBonus: 0.25, defenceRadius: 5,
    zapDamage: 0, zapRange: 0, troopCap: 0, trains: [UnitType.Wyvern],
    desc: 'A nest of wild dragonkin on a pile of stolen treasure.',
  },
  {
    key: 'griffinnest', name: 'Griffin Nest', hp: 360, cost: [0, 0, 0, 0, 0], preferred: null,
    buildable: false, captureable: false, limit: 1,
    income: [0, 0, 0, 0, 0], popCap: 0, defenceBonus: 0.2, defenceRadius: 5,
    zapDamage: 0, zapRange: 0, troopCap: 0, trains: [UnitType.Griffin],
    desc: 'An eyrie of griffins. They see everything that moves nearby.',
  },
  /* ------------------------------------------------------------ storage */
  {
    key: 'warehouse', name: 'Warehouse', hp: 260, cost: [60, 200, 140, 0, 110], preferred: null,
    limit: 4,
    income: [0, 0, 0, 0, 0], popCap: 15, defenceBonus: 0, defenceRadius: 0,
    zapDamage: 0, zapRange: 0, troopCap: 0, trains: [],
    desc: `Raises how much of everything your realm can hold, by ${WAREHOUSE_CAP} per level. ` +
      'Without one you simply stop gaining the moment the storehouses are full.',
  },
];

for (const def of STRUCTURE_DEFS) {
  if (def.buildable === false || def.key === 'keep') continue;
  def.limit = UNLIMITED;
}

/** Bots keep a soft ceiling purely to protect the server tick budget. */
export const BOT_STRUCTURE_SOFT_CAP = 34;

/**
 * How far a build order may be nudged to a tile that can actually take it.
 * Clicking is aiming, not surveying: asking for a Harbour on a beach should put
 * one on that beach, not fail because the exact pixel had no water beside it.
 */
export const BUILD_SNAP_RADIUS = 4;
/**
 * How far a build click reaches to find one of *your own* buildings of the same
 * type to level up instead.
 *
 * Buildings are one tile on a map you are usually looking at from far away, so
 * "upgrade this Village" and "put a second Village beside it" differ by a couple
 * of screen pixels — and the wrong one of those costs you a full building's
 * price. Landing anywhere within two tiles means the upgrade, which is what a
 * player who clicked at a building meant.
 *
 * A click that lands squarely *on* one of your own does not go through this at
 * all: `placementFor` treats that as an unambiguous order about that building,
 * and refuses rather than quietly buying a second one somewhere else.
 */
export const UPGRADE_SNAP_RADIUS = 2;

/**
 * How far apart the standing places in a formation are, in tiles.
 *
 * Comfortably wider than the separation floor (`UNIT_SEPARATION` keeps two
 * bodies about 0.8 apart) so that a unit standing in its slot is not touching
 * its neighbours — that gap is the whole reason an arrived squad holds still
 * instead of jostling.
 */
export const FORMATION_SPACING = 1.15;
/** 137.5°: the angle sunflowers use, and the only one that never lines up. */
export const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
export const TRADE_POST_BONUS = 0.04;
export const TRADE_POST_BONUS_CAP = 0.20;
export const GIFT_TAX = 0.10;
export const GIFT_COOLDOWN = 2;

/**
 * **Sending your people to a team-mate costs nothing but time.**
 *
 * Goods travel — a relief caravan can be robbed on the road, which is why aid
 * is taxed. People sent to an ally are not modelled as a march at all: the men
 * are simply on their side of the line now. Skimming a fifth off the top for
 * flavour only taught players to distrust the button, so every man offered who
 * fits under their ceiling arrives.
 *
 * What stops it being a teleport army is the clock. Eight seconds **per
 * recipient**: enough to prop up a friend who is losing a border, far too slow
 * to shuttle a reserve back and forth across a war as it happens.
 */
export const REINFORCE_COOLDOWN = 8;         // seconds, per recipient

/**
 * **What burning a loaded caravan is worth: a quarter of it, and no more.**
 *
 * One number for every kind of cart on the map — the gold on a trade wagon, the
 * gold in a merchantman's hold, the crates in a relief convoy. There is no
 * reason for a raider to be paid three different ways for the same act, and
 * there never was a reason for the plain trade wagon to be the *best* of the
 * three: killing one used to hand over its whole load, which made hunting
 * traffic the cheapest income in the game and a defended trade route the most
 * expensive thing a realm could own.
 *
 * A quarter keeps the raid worth making and stops it being worth making
 * **instead of** an economy. The other three quarters are not transferred to
 * anybody: they burn. That is the point of the number — an ambush is mostly a
 * denial, and only incidentally a payday, so a war on somebody's roads costs
 * them far more than it earns you.
 *
 * Aid convoys ride the same rule: sending goods to an ally is not a teleport,
 * **both** of you need a Market, the load leaves your storehouse at once, and a
 * real caravan walks it across the map. Generosity between neighbours is cheap;
 * generosity across a war zone is a gamble, and losing that gamble now costs the
 * sender everything while paying the ambusher a quarter.
 */
export const CARAVAN_LOOT_SHARE = 0.25;
export const AID_MAX_PER_RESOURCE = 600;
export const AID_TIMEOUT = 260;
export const AID_COOLDOWN = 8;

/* ------------------------------------------------------------ race table */

export interface RaceDef {
  key: string;
  name: string;
  tagline: string;
  desc: string;
  /** every kingdom of this race starts with this building for free? (none today) */
  tileDefence: number;      // multiplier on how expensive their land is to take
  ignoreTerrainSpeed: boolean;
  /**
   * Declared, never read: `cmdGift` waives the tax for anyone who owns a
   * Market, which is a building decision rather than a birthright. Left in
   * place as the hook it was meant to be, and deliberately not claimed in any
   * player-facing text until something reads it.
   */
  giftTaxFree: boolean;
  income: number[];         // multiplier per resource on *buildings*
  troopGrowth: number;      // multiplier
}

export const RACE_DEFS: RaceDef[] = [
  {
    key: 'human', name: 'Humans', tagline: 'Traders and generalists',
    desc: 'No weakness and no speciality. Their markets pay a little better, they hold the open ' +
      'plain 10 % harder than strangers do, and they field the only Dragons on the map.',
    tileDefence: 1, ignoreTerrainSpeed: false, giftTaxFree: true,
    income: [1, 1, 1, 1, 1.1], troopGrowth: 1,
  },
  {
    key: 'dwarf', name: 'Dwarves', tagline: 'Slow, rich, immovable',
    desc: 'Everything they own is harder to take (+20 % tile defence, +10 % again in the ' +
      'mountains) and their quarries run 30 % hotter, but their soldiers are slow and their ' +
      'Shieldbearers cannot strike a blow. Battle Golems instead of Dragons.',
    tileDefence: 1.2, ignoreTerrainSpeed: false, giftTaxFree: false,
    income: [1, 1, 1.3, 1, 1], troopGrowth: 0.9,
  },
  {
    // "far-seeing" went with the thieves: it promised detection, and there is
    // nothing hidden on the map for anyone to see any more
    key: 'elf', name: 'Elves', tagline: 'Fast, fragile, sure-footed',
    desc: 'Forest and mountain do not slow them at all, they hold the forest 10 % harder than ' +
      'anyone else, their lumber runs 30 % hotter, and they take ground faster than anyone — on ' +
      'thinner armies. Phoenixes rise again once.',
    tileDefence: 0.92, ignoreTerrainSpeed: true, giftTaxFree: false,
    income: [1, 1.3, 1, 1, 1], troopGrowth: 1.05,
  },
];

/**
 * The **three** military halls of each people, by the role they fill.
 *
 * There was a fourth — the rogue hall — and it went with the thieves. Every
 * people now has a line hall, a caster hall and an elite hall, which is one
 * fewer building to price, one fewer to learn, and one fewer decision that
 * mostly came down to "yes, eventually".
 */
export const RACE_HALLS: Record<number, { war: StructureType; caster: StructureType; elite: StructureType }> = {
  [Race.Human]: {
    war: StructureType.Barracks,
    caster: StructureType.MageTower, elite: StructureType.DragonRoost,
  },
  [Race.Dwarf]: {
    war: StructureType.ForgeHall,
    caster: StructureType.RuneHall, elite: StructureType.GolemFoundry,
  },
  [Race.Elf]: {
    war: StructureType.BowyerGlade,
    caster: StructureType.MoonGrove, elite: StructureType.PhoenixAerie,
  },
};

/** Every structure a race is allowed to build. */
export function racialStructures(race: Race): StructureType[] {
  const out: StructureType[] = [];
  STRUCTURE_DEFS.forEach((def, type) => {
    if (def.buildable === false || type === StructureType.Keep) return;
    if (def.race !== undefined && def.race !== race) return;
    out.push(type as StructureType);
  });
  return out;
}

/* ---------------------------------------------------------------- editions */

/**
 * **Light: the same game with less of it.**
 *
 * Not a different ruleset — that is the whole point, and the one thing this
 * section enforces. Every mechanic behaves identically in both editions: the
 * same conquest maths, the same walls, the same trade, the same victory line,
 * the same numbers on every card. What Light removes is *content*.
 *
 * A new player's opening minute in the full game contains three peoples with
 * different buildings, five resources — one of which comes only from a terrain
 * they have to go and find — four military halls each with its own roster, and
 * a tower that shoots on its own. That is four systems to learn before the
 * first one has landed. Light is humans, four resources, one barracks: every
 * skill it teaches is worth exactly as much in the full game, because none of
 * the rules changed. The player just meets them one at a time.
 *
 * A room carries its edition (`RoomConfig.light`) and worlds never mix, so the
 * two are separate lobbies rather than a setting inside a match.
 */
export const LIGHT_RACES: Race[] = [Race.Human];

/**
 * What a Light world does not build. Read it as four removals, not eleven:
 *
 *  - **every dwarven and elven building**, because there are no dwarves or elves
 *  - **the Ley Shrine**, mana's only source in the game — so dropping one
 *    building drops a whole resource without touching a single rule
 *  - **every military hall except the Barracks**, so "where do soldiers come
 *    from" has exactly one answer
 *  - **the Watchtower**, the only building that fights and the only one whose
 *    worth is an aura rather than a number on its card
 *
 * Derived rather than listed, so a building added to the game lands on the
 * right side of the line by construction.
 */
export const LIGHT_BANNED_STRUCTURES: ReadonlySet<StructureType> = (() => {
  const out = new Set<StructureType>();
  STRUCTURE_DEFS.forEach((def, type) => {
    if (def.race !== undefined && !LIGHT_RACES.includes(def.race)) out.add(type);
    if (def.income[Res.Mana] > 0) out.add(type);
    if (def.trains.length > 0 && type !== StructureType.Barracks) out.add(type);
  });
  out.add(StructureType.Watchtower);
  /*
   * And the Warehouse, which exists only to raise a ceiling a Light world does
   * not have — see `storageCapped`. Leaving it in would be a building whose
   * whole card describes a rule that is not in the game.
   */
  out.add(StructureType.Warehouse);
  return out;
})();

/**
 * **Nothing has a storage ceiling any more, in either edition.**
 *
 * The full game used to cap every resource at `RES_CAP_BASE` and sell more of
 * it a Warehouse at a time, while Light had no ceiling and no Warehouse. The
 * pair went together, and the comment here said so: remove one without the
 * other and you are left with either a building that does nothing or **a wall
 * with no door in it**.
 *
 * The Warehouse has now been retired (`RETIRED_STRUCTURES`), so this had to move
 * with it — and it very nearly did not, which is exactly the failure the old
 * comment predicted: for one commit the full game sat at a hard 3 000 with no
 * way on earth to raise it, while a single race power costs twelve thousand of
 * each resource. Unbuyable by arithmetic.
 *
 * What is left is the rail rather than the rule: `RES_HARD_CAP` bounds the
 * number so nothing downstream meets an `Infinity`, and no player ever reaches
 * it. Hoarding is kept in check by the thing that always actually did it —
 * prices that escalate faster than any stockpile grows.
 */
export function storageCapped(_light: boolean): boolean { return false; }

/**
 * **Retired buildings — gone from every edition, in the game's memory only.**
 *
 * The Warehouse went the way the Thieves' Den went, and for the same reason:
 * it existed to solve a problem that no longer exists. Its whole job was
 * raising a storage ceiling, and a ceiling nobody can reach is not a thing to
 * sell a building against — so what was left was a building you had to know to
 * build, that did nothing you could see.
 *
 * **The enum value stays.** `StructureType` is the sprite index, the art-table
 * key and the byte on the wire all at once; deleting an entry would silently
 * renumber every building after it. Removed means "nothing may raise one",
 * never "the number is free".
 */
const RETIRED_STRUCTURES = new Set<StructureType>([StructureType.Warehouse]);

/** May this edition raise this building at all? The Keep is nobody's choice. */
export function structureInEdition(type: StructureType, light: boolean): boolean {
  if (RETIRED_STRUCTURES.has(type)) return false;
  return !light || !LIGHT_BANNED_STRUCTURES.has(type);
}

/**
 * May this edition field this soldier?
 *
 * Asked of the hall it comes out of rather than of the unit, so the two can
 * never disagree — a unit exists in an edition exactly when something that can
 * train it does. Civilians have no hall to check and belong to everybody.
 */
export function unitInEdition(type: UnitType, light: boolean): boolean {
  if (!light) return true;
  const def = UNIT_DEFS[type];
  if (!def || !def.trainable) return true;          // camp creatures, caravans, hulls
  return def.from.some(s => structureInEdition(s, true));
}

/** Which peoples an edition fields. */
export function racesInEdition(light: boolean): Race[] {
  return light ? LIGHT_RACES : RACE_DEFS.map((_, i) => i as Race);
}

/**
 * Is mana a thing in this edition?
 *
 * A consequence of the Ley Shrine being gone rather than a rule of its own —
 * but the HUD, the price rows and the map legend all need the answer, and they
 * should ask once here rather than each work it out from the building list.
 */
export function manaInEdition(light: boolean): boolean { return !light; }

/** Every unit a race can train. */
export function racialUnits(race: Race): UnitType[] {
  const out: UnitType[] = [];
  UNIT_DEFS.forEach((def, type) => {
    if (!def.trainable) return;
    const home = STRUCTURE_DEFS[def.from[0]];
    if (home.race !== undefined && home.race !== race) return;
    out.push(type as UnitType);
  });
  return out;
}

export function raceOfStructure(type: StructureType): Race | undefined {
  return STRUCTURE_DEFS[type]?.race;
}

/* --------------------------------------------------------------- pricing */

export function terrainFactor(def: StructureDef, terrain: number, race?: Race): number {
  const preferred = (race !== undefined && def.preferredByRace)
    ? def.preferredByRace[race] : def.preferred;
  if (!preferred) return 1;
  return preferred.includes(terrain as Terrain) ? 1 : WRONG_TERRAIN_FACTOR;
}

/** The ground this race's villages want, for the HUD's "best on …" line. */
export function preferredTerrainFor(def: StructureDef, race?: Race): Terrain[] | null {
  if (race !== undefined && def.preferredByRace) return def.preferredByRace[race];
  return def.preferred;
}

/**
 * How much a building is worth against how much ground is worth.
 *
 * Kept as one multiplier rather than baked into the tables, because it is the
 * dial for the whole "develop or expand?" question: raise it and a compact
 * built-up realm wins the economy, lower it and painting tiles does. Every
 * consumer — the economy loop and the HUD alike — goes through `structureIncome`
 * so the number the player is shown is the number they actually get.
 */
export const STRUCTURE_INCOME_SCALE = 3.2;

/**
 * What one level of a building really produces per second, after the global
 * pace. The tables above are written at the old speed so they stay readable.
 */
/**
 * **The trades that get better the more you commit to them.**
 *
 * Only these three: Farm, Lumber Camp, Quarry — the buildings whose whole job is
 * pulling a raw resource out of the ground. Villages, markets and halls are left
 * alone deliberately; their output is population, tax and army capacity, and
 * putting a growth curve under those is a different balance argument.
 */
const YIELD_TRADES = new Set(['farm', 'lumber', 'quarry']);

/**
 * **A specialist earns more per barn than a dabbler — but nothing like enough
 * to keep up with the price.**
 *
 * The two curves were badly mismatched. Price compounds at `BUILDING_COST_STEP`
 * (1.6× per level of that type already held), and output was *flat*: the tenth
 * farm level cost sixty-eight times the first and produced exactly as much. So
 * past three or four of a kind there was never a reason to build another, and
 * "specialise in grain" was not a strategy the arithmetic allowed.
 *
 * **Matching the price curve is not the fix.** At 1.6 a realm holding twenty
 * farm levels would see the twentieth produce six thousand times the first;
 * every stockpile in the game would sit pinned at its cap inside a minute. The
 * request was for output to climb *like* the price climbs, and the honest
 * reading of that is "climb, and be worth it", not "climb at the same rate" —
 * which is why this is deliberately gentle and, unlike the price, **capped**.
 *
 * **What the harness can and cannot tell you about this.** A first attempt at
 * 1.07 was measured against the baseline on one seed and appeared to stretch
 * the war from 36.0 minutes to 50.5. That reading does not survive a second
 * run: at the *gentler* 1.04 the same seed did not resolve inside sixty minutes
 * at all. A smaller bonus cannot systematically produce a longer war, so what
 * both runs measured was the simulation's own chaos, not the constant. The sim
 * is deterministic — there is no `Math.random` in the server — but a changed
 * constant puts a hundred kingdoms on an entirely different trajectory, and
 * `simspread` already puts the natural spread at 31.5–52.6 minutes across
 * seeds. **Match length here needs `npm run simtest:spread` over many seeds; a
 * single run is worth nothing.**
 *
 * **Straight-line, and it starts below where it used to sit.** One level of a
 * trade yields 80 % of the table, and every further level of that trade adds
 * ten points to *all* of them:
 *
 * | levels held | each yields | total |
 * |---|---|---|
 * | 1 | ×0.8 | 8 |
 * | 2 (or one at level 2) | ×0.9 | 18 |
 * | 3 | ×1.0 | 30 |
 * | 4 | ×1.1 | 44 |
 *
 * Reading the middle row matters: two separate barns and one barn raised to
 * level two are the same realm as far as this is concerned, because both are
 * two levels of the same trade — the same number the price escalation reads.
 *
 * The dip at one level is the point, not an accident. Flat output made the
 * fourth barn pointless; simply adding a bonus on top would have made every
 * realm richer than the game is tuned for. Starting at 0.8 and crossing the old
 * flat rate at the *third* level means a realm that spreads itself thin earns
 * slightly less than it used to, a typical three-building realm earns exactly
 * what it always did, and only genuine specialisation is ahead. Total income
 * across a match barely moves; what moves is which choice pays.
 *
 * **Uncapped, on purpose.** A ceiling here would be a second brake bolted on
 * top of one that already works: the price compounds at 1.6, so a twentieth
 * level costs six thousand times the first. Ten points a level against sixty
 * per cent a level is not a race the yield can win, so it needs no finish line.
 */
export const YIELD_TRADE_AT_ONE = 0.8;
export const YIELD_TRADE_PER_LEVEL = 0.1;

export function yieldScale(key: string, levelsOwned: number): number {
  if (!YIELD_TRADES.has(key)) return 1;
  return YIELD_TRADE_AT_ONE + YIELD_TRADE_PER_LEVEL * (Math.max(1, levelsOwned) - 1);
}

/**
 * What one level of a building really produces per second, after the global
 * pace and — for the three raw trades — the specialist's bonus.
 *
 * `levelsOwned` is how many levels of this *type* the owner holds in total, the
 * same number the price escalation reads. Left at zero it returns the bare
 * figure, which is what a table or a generic description wants.
 */
export function structureIncome(def: StructureDef, r: number, levelsOwned = 0): number {
  const base = (def.income[r] || 0) * GAME_PACE * STRUCTURE_INCOME_SCALE;
  return levelsOwned > 0 ? base * yieldScale(def.key, levelsOwned) : base;
}

/**
 * One dial over every building price, new or levelled.
 *
 * Buildings carry the economy now, so they have to be worth saving up for; this
 * is the knob for that, kept separate from the per-building tables so the tables
 * stay a statement of *relative* worth and this stays a statement of how hard
 * the economy squeezes.
 */
export const BUILDING_PRICE_SCALE = 1.25;

/**
 * **What the next unit of a building type costs — however you take it.**
 *
 * `owned` is the total number of *levels* you already hold of that kind, not
 * the number of buildings. Raising a new hall and adding a floor to an old one
 * both buy you exactly one level's worth of housing, output and capacity, so
 * both are charged exactly the same and both make the next one dearer by the
 * same step.
 *
 * They used to run on two different curves — new buildings compounding at 1.6,
 * levels climbing linearly — which made a second building cheaper than a second
 * level and a fifth building dearer than a fifth level. Nobody could have
 * derived that from the interface, and it made "build or upgrade?" a question
 * about a formula rather than about the map.
 *
 * Now the only thing that separates them is *placement*: a new building stands
 * somewhere else, on its own terrain, with its own aura and its own risk of
 * being taken. That is a real decision. Price is not part of it.
 */
/**
 * **The ceiling on any one resource in any one building's price.**
 *
 * A safety rail, like `RES_HARD_CAP`, and for the same reason: the escalation
 * compounds at 1.6 and nothing stopped it. The fifteenth level of a trade
 * already asks a thousand times the first, the twentieth six thousand times —
 * numbers that stop being a price and start being a way of saying "no", badly.
 * A hundred thousand is far past what any realm can hold or earn in a match, so
 * it never decides anything; it just means the figure on the button stays a
 * figure.
 */
export const BUILDING_COST_MAX = 100_000;

export function buildingCost(def: StructureDef, owned: number): number[] {
  const scale = Math.pow(BUILDING_COST_STEP, Math.max(0, owned)) * BUILDING_PRICE_SCALE;
  return def.cost.map(c => Math.min(BUILDING_COST_MAX, roundPrice(c * scale)));
}

/** The same price, reached the other way. Kept as a name for what it means. */
export function levelCost(def: StructureDef, ownedLevels: number): number[] {
  return buildingCost(def, ownedLevels);
}

/**
 * Gold is a joker for the mundane resources (1 gold = 1 food/wood/stone) but
 * never for mana. Returns whether the price can be met and how much gold it eats.
 */
export function priceWithGold(cost: number[], have: ArrayLike<number>): { ok: boolean; goldNeeded: number } {
  let goldNeeded = cost[Res.Gold] || 0;
  for (let r = 0; r < RES_COUNT; r++) {
    if (r === Res.Gold) continue;
    const short = (cost[r] || 0) - have[r];
    if (short <= 0) continue;
    if (GOLD_CANNOT_BUY.includes(r)) return { ok: false, goldNeeded: Infinity };
    goldNeeded += short * GOLD_PER_RESOURCE;
  }
  return { ok: have[Res.Gold] >= goldNeeded, goldNeeded };
}

/* ---------------------------------------------------------------- walls */

/**
 * Walls are not buildings.
 *
 * There can be a thousand of them, they never train anything, never produce
 * anything and never change hands — so they live in flat per-tile arrays beside
 * ownership and terrain rather than in the structure map. What they do is
 * simple and absolute: **territory cannot be taken through a standing wall.**
 * An advancing front stops at the stonework and grinds it down; soldiers tear
 * it down far faster. Either way the wall has to fall before the land behind it
 * can change hands, which is the whole point of building one.
 *
 * A **gate** is a wall you can walk through — but only if you are its owner or
 * their ally. It blocks conquest exactly like a rampart does and is a little
 * weaker, so a wall line's doors are also its soft spots.
 */
export enum WallKind { None = 0, Rampart = 1, Gate = 2 }
export const WALL_KIND_NAME = ['', 'Wall', 'Gate'];

export const WALL_HP = 620;
export const GATE_HP = 440;
/*
 * There was a `WALL_TOWER_HP_FACTOR` here, worth 1.6: a Watchtower raised on a
 * rampart became a bastion and the stonework under it took that much more
 * punishment. The bastion is retired — nothing stands on a wall now, from
 * either direction — and the constant went with it rather than sitting here
 * describing a mechanic the game no longer has. Unlike a `StructureType` or an
 * `Fx`, a number nobody sends down the wire can simply go.
 */

/** Per segment. Cheap on its own — a wall is priced by the *mile*, below. */
export const WALL_COST = [0, 26, 52, 0, 12];
export const GATE_COST = [0, 55, 70, 0, 40];
/**
 * The price does **not** climb per segment — a wall that got dearer every tile
 * would punish drawing a line at all, and the whole point of a wall is that it
 * is long. It climbs per **block of twenty**: the first twenty cost what they
 * say, the next twenty a third more, and so on. Fortifying a border is
 * affordable; fortifying a continent is not.
 */
export const WALL_PRICE_BLOCK = 20;
export const WALL_PRICE_STEP = 0.35;
/** How many segments one drag may lay, so a single message cannot be enormous. */
export const WALL_MAX_PER_ORDER = 96;

export function wallCost(owned: number, gate: boolean): number[] {
  const tier = Math.floor(Math.max(0, owned) / WALL_PRICE_BLOCK);
  const scale = 1 + tier * WALL_PRICE_STEP;
  return (gate ? GATE_COST : WALL_COST).map(c => Math.round(c * scale));
}

export function wallBaseHp(kind: WallKind): number {
  return kind === WallKind.Gate ? GATE_HP : WALL_HP;
}

/**
 * How far a soldier will step out of its way to knock a wall down. Short on
 * purpose — an army notices the rampart it has walked into, not one across the
 * valley — and it keeps the scan that finds walls cheap.
 */
export const WALL_AGGRO_RANGE = 4;

/**
 * **Only trained units bring a wall down. An attack does nothing to it at all.**
 *
 * The men in your troop bar are a number, not a body of people standing in a
 * field with picks: they take ground, and stonework is not ground. An assault
 * that runs into a wall simply has nowhere left to go and marches home.
 *
 * So a wall converts an enemy's *money* into an enemy's *time*: to get through
 * they must train something, walk it to the wall, and spend it on masonry
 * instead of on your army. That trade is the whole reason a fortified border is
 * worth the stone — and the reason a wall you never garrison is still worth
 * having.
 */
export const WALL_UNIT_DAMAGE = 2.2;
/**
 * What one tick of a flier's razing takes out of a wall it is hovering over.
 * Three passes bring a rampart down, which makes dragons and phoenixes the
 * fastest — and by far the dearest — way through a fortified border.
 */
export const WALL_RAZE_SHARE = 0.34;

/**
 * **A wall needs a kingdom behind it.**
 *
 * Stonework does not defend itself: somebody has to stand on it. A realm can man
 * one segment for every `WALL_TILES_PER_SEGMENT` tiles it holds, and everything
 * beyond that is abandoned — it crumbles at `WALL_DECAY_HP` a second until the
 * line matches the country.
 *
 * This exists for the same reason `DEFENCE_DENSITY_CAP` does. A kingdom beaten
 * back to a handful of tiles was left *indigestible*: an attack cannot touch a
 * wall at any size, so a rump behind stone could only be dug out by soldiers,
 * and a hundred bot kingdoms fielding five units each never got round to it.
 * Matches stopped resolving. Now losing your country loses you your walls with
 * it, which is both the fix and the truth.
 */
/*
 * Tried at 20 to break the fortification stalemate, and **put back at 6 because
 * the measurement could not tell whether it worked.** One world went from 45.1
 * minutes and 1276 standing segments to 40.7 and 459 — exactly the predicted
 * effect. The next world went from 45.8 minutes to *not resolving at all*, with
 * its wall count going **up**, 888 to 1697. Two worlds, two opposite answers, on
 * a change that also takes a defensive tool away from players. Not enough to
 * ship on. See the note above `ATTACK_BASE_COST` for why three seeds cannot
 * settle a question this noisy.
 */
export const WALL_TILES_PER_SEGMENT = 6;
export const WALL_DECAY_HP = 45;

/**
 * How much of the border it is **actually being attacked on** a bot may wall.
 *
 * The hostile frontier, not the whole border ring — that distinction was earned
 * twice. Priced off the ring, a kingdom with a long shore could wall its entire
 * land border several times over, because most of a ring is coastline and
 * riverbank with nobody on the far side; a hundred bots doing that gridlocked the
 * map, and since an attack cannot touch stonework the war simply stopped.
 *
 * At a quarter of the contested frontier a bot's border carries a few fortified
 * stretches — enough that you have to bring an army rather than click through it
 * — and three quarters of the fighting front stays open. A **player** has no such
 * limit, because a player can see what they are doing to themselves.
 */
export const BOT_WALL_BORDER_SHARE = 0.25;
export const BOT_WALL_MAX = 40;
/** Bots build in short runs along the frontier, with a gate in the middle. */
export const BOT_WALL_RUN = 7;
/** Spare wood and stone a bot keeps back before it spends any on masonry. */
export const BOT_WALL_RESERVE = 260;
/** Nothing is worth fortifying before there is a realm to fortify. */
export const BOT_WALL_MIN_TILES = 300;

/* --------------------------------------------------------------- repairs */

/**
 * The standing repair order: masons at work, paid by the hour.
 *
 * **A realm repairs at one rate, not each building at its own** — a single
 * budget of HP per second, spread over whatever is damaged, fortifications
 * first. That is what makes it a *slow, continuous* trickle rather than a heal
 * button: it visibly cannot out-mend an assault (four knights take a rampart
 * apart five times faster than this puts it back), but it does bleed an
 * attacker's progress while they work, and it quietly puts a province back
 * together between wars without a single click.
 *
 * It is paid **only in gold**, because gold is the resource you can always spend
 * and never quite have enough of — so the toggle competes directly with your
 * army and your buildings for the same coin.
 */
/**
 * How often a standing garrison re-posts itself along the frontier. Two seconds
 * — a front moves at most a few tiles a second, and re-walking the border ring
 * any more often would be paying for precision nobody can see.
 */
export const DEFEND_REPOST_TICKS = 20;

/**
 * How far a garrisoned soldier will step from his post to hit something.
 *
 * Without a leash "hold the line" was a lie: a man posted on the border could
 * see an enemy rampart a few tiles into their land, walk over to pull it down,
 * and leave the gap he was standing in wide open. A standing order to defend
 * has to mean *defend* — engage what comes to you, and do not go looking.
 *
 * Measured from the post rather than from the soldier, so a garrison cannot
 * inch its way across the map one acquisition at a time.
 */
export const DEFEND_LEASH = 3.5;

export const REPAIR_HP_PER_SECOND = 26;
/** What one point of health costs on an ordinary building. */
export const REPAIR_GOLD_PER_HP = 0.09;
/**
 * And what it costs on a **wall, a gate or a Watchtower** — six times as much.
 *
 * This is the counterweight to the whole defensive mechanic. Without it, "repair
 * on" plus a walled border would be a fortress that mends itself for pocket
 * change; at this price holding a rampart under siege eats a third of a mid-game
 * treasury, so keeping a wall standing is a decision you have to keep paying for.
 */
export const REPAIR_GOLD_PER_HP_FORT = 0.55;

/* -------------------------------------------------------- monster camps */

export const CAMP_DENSITY = 1400;
export const CAMP_MIN = 24;
export const CAMP_MAX = 120;
export const CAMP_RADIUS = 3;
/**
 * How much elbow room a camp demands from a **new kingdom** — none beyond its
 * own palisade. A warband is a hazard, not a neighbour: settling with orcs at
 * the bottom of the garden is a trade (a dangerous first ten minutes for a
 * hoard worth taking), and the game has no business forbidding it.
 */
export const CAMP_SPAWN_CLEARANCE = CAMP_RADIUS;
export const CAMP_MIN_DISTANCE = 20;
/**
 * How much less willing a new warband is to settle on claimed ground. At 0.65 a
 * site inside somebody's borders is rejected roughly two times in three, so
 * camps overwhelmingly appear in the wilds — and never on top of a building.
 */
export const CAMP_SETTLED_PENALTY = 0.65;
export const CAMP_TROOPS = 150;
export const CAMP_RESPAWN_MIN = 70;
export const CAMP_RESPAWN_MAX = 140;
export const CAMP_MAX_UNITS = 3;
export const CAMP_UNIT_INTERVAL = 26;
export const CAMP_LEASH = 16;
export const CAMP_MAX_TILES = 90;
export const CAMP_FORAGE_FACTOR = 0.25;
export const CAMP_PLUNDER_GOLD = 0.06;
export const CAMP_HOARD_CAP = 1200;

export const CAMP_TYPES: { type: StructureType; weight: number; coastal: boolean; name: string; hue: number }[] = [
  { type: StructureType.OrcCamp, weight: 32, coastal: false, name: 'Orcs', hue: 96 },
  { type: StructureType.GoblinWarren, weight: 28, coastal: false, name: 'Goblins', hue: 62 },
  { type: StructureType.MurlocLagoon, weight: 18, coastal: true, name: 'Murlocs', hue: 190 },
  { type: StructureType.GriffinNest, weight: 14, coastal: false, name: 'Griffins', hue: 38 },
  { type: StructureType.DragonNest, weight: 8, coastal: false, name: 'Wyverns', hue: 12 },
];

export function pickCampType(roll: number) {
  const total = CAMP_TYPES.reduce((a, c) => a + c.weight, 0);
  let x = roll * total;
  for (const c of CAMP_TYPES) {
    x -= c.weight;
    if (x <= 0) return c;
  }
  return CAMP_TYPES[0];
}

/* -------------------------------------------------------------- diplomacy */

export enum Relation { Self = 0, Team = 1, Ally = 2, Enemy = 3, Neutral = 4 }
export const ALLY_REQUEST_TTL = 30;
export const BETRAYAL_WARNING = 5;

/* ------------------------------------------------------------------- teams */

export interface TeamDef { name: string; hue: number; }

export const TEAM_DEFS: TeamDef[] = [
  { name: 'Crimson', hue: 0 },
  { name: 'Azure', hue: 212 },
  { name: 'Emerald', hue: 145 },
  { name: 'Gold', hue: 45 },
  { name: 'Violet', hue: 278 },
  { name: 'Teal', hue: 180 },
  { name: 'Amber', hue: 24 },
  { name: 'Rose', hue: 330 },
];

/* ----------------------------------------------------------------- effects */

/**
 * **Append only.** These values go on the wire as a raw byte (`fx.u8(e.type)` in
 * `encodeEntities`), so an insertion in the middle silently re-labels every
 * effect after it — the client would draw a meteor where a caravan unloaded and
 * nothing would look broken enough to notice. New kinds go on the end.
 *
 * A value the other side does not know about is harmless in both directions:
 * the client's `switch` has no `case` for it and simply draws nothing, which is
 * why adding one is not a `PROTOCOL_VERSION` change.
 *
 * `Steal = 8` is dead. It went with the thieves and is kept only so the numbers
 * after it do not move.
 */
export enum Fx {
  Hit = 0,
  Arrow = 1,
  Fireball = 2,
  Meteor = 3,
  Zap = 4,
  Death = 5,
  Capture = 6,
  Build = 7,
  Steal = 8,
  Raze = 9,
  Spawn = 10,
  StructureDown = 11,
  Trade = 12,
  Loot = 13,
  Upgrade = 14,
  Heal = 15,
  Rebirth = 16,
  /** two armies marching into each other and annihilating */
  Clash = 17,
  /** one strike of a race power; `param` carries the race, so each looks its own */
  Wrath = 18,
  /**
   * The Colossus itself, standing where it stands.
   *
   * Emitted every step alongside its strike so the machine is *on the map*
   * rather than merely implied by the craters it leaves. Twenty-two seconds of
   * something you paid thirty-six thousand for ought to be visible from across
   * the province.
   */
  Colossus = 19,
  /**
   * **A kingdom ending.**
   *
   * The one moment in a match that had a line of text and no picture: the
   * notice said "X has been wiped from Kaelmoor" while the map showed only
   * colour quietly draining off it. Emitted at the fallen realm's capital, so
   * whoever is looking that way sees the thing happen rather than reading about
   * it afterwards.
   *
   * Drawn in fixed ash and ember rather than the owner's colour, because the
   * client reads an effect's colour off *the tile it lands on* and that ground
   * has just gone back to the wilderness — asking for the realm's colour here
   * would reliably paint the death of a kingdom bone white.
   */
  Downfall = 20,
  /**
   * **An opening truce running out.**
   *
   * `SPAWN_PROTECT_SECONDS` of grace end on a silent tick: nothing on screen
   * changes, and the first a player knows of it is somebody's army arriving.
   * One ring at the capital, in the realm's colour, is the whole warning — and
   * it is equally worth seeing on a *neighbour*, because it says the door just
   * opened.
   */
  Unshielded = 21,
}
export const MAX_EFFECTS_PER_TICK = 220;

/**
 * Bits in a unit's snapshot flag byte. Shared so both ends agree.
 *
 * 4 and 16 are free: they were `STEALTH` and `REVEALED`, and went with the
 * thieves. Values are left where they are rather than closed up, because a bit
 * that changes meaning between builds is the worst kind of protocol change —
 * both ends look correct and the picture is wrong.
 */
export const UNIT_FLAG_FACING_LEFT = 1;
export const UNIT_FLAG_FLASH = 2;
export const UNIT_FLAG_HAS_ORDER = 8;
export const UNIT_FLAG_SPARE_BUILDINGS = 32;
/**
 * A varint follows the flag byte: how many troops this transport is carrying.
 * Only ever set for your own and your allies' boats — an enemy ship is a hull
 * on the horizon, and how many men are inside it is exactly the thing you are
 * not supposed to know.
 */
export const UNIT_FLAG_CARGO = 64;

/* -------------------------------------------------------------- networking */

export enum Msg {
  Snapshot = 1,
  Entities = 2,
  Players = 3,
  TerritoryFull = 4,
  TerritoryDelta = 5,
}

export enum Phase { Lobby = 0, Spawn = 1, Live = 2, Results = 3 }

/* ------------------------------------------------------------- ping types */

export enum PingKind { Attack = 0, Defend = 1, NeedHelp = 2 }
export const PING_LABEL = ['Attack here!', 'Defend here!', 'Need resources!'];

/**
 * **A colour per kind of ping — red to strike, blue to hold, green to send.**
 *
 * The mark on the map used to be drawn purely in the *sender's* colour, which
 * answered "who" and left "what" to a label nobody reads across a continent.
 * With three fixed meanings and no chat to explain them, the meaning is the
 * part worth carrying in colour.
 *
 * Both ends use this: the button you press and the ring that lands. A button
 * whose colour is not the colour of the thing it produces is a small lie the
 * player has to learn around.
 */
export const PING_COLOR = [0xe86a5c, 0x6aa8e8, 0x8ed17a];
